/**
 * Elite Batch 19 Part 4 — Workflow Templates 1808–1827
 * Industry analyst briefing, influencer partnership outreach, integration partner
 * outreach, Intercom ticket enrichment, investor relations update, job posting
 * personalization, job-change trigger outreach, journalist pitch outreach, key
 * decision-makers from URL, lead source normalization, legal stakeholder engagement,
 * link prospect outreach, LinkedIn keyword search build, LinkedIn post engager
 * capture, local business partnership, local businesses for paid-ads outreach,
 * local businesses for SEO outreach, lost backlink reclaim, MEDDICC extraction
 * from call, and Marketo lead create update workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 19 PART 4 — TEMPLATES 1808–1827
// =============================================================================

export const ELITE_BATCH19_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1808. INDUSTRY ANALYST BRIEFING (7 nodes)
  // ===================================================================

  "Industry Analyst Briefing": {
    summary:
      "Runs on a weekly schedule to identify relevant industry analysts with AI, assess coverage alignment, generate tailored briefing requests, resolve analyst contact details via API, validate email addresses, send the briefing request, and log analyst-relations activity to Sheets.",
    nodes: [
      trigger("Schedule: Weekly analyst briefing prep", "schedule", {
        source: "analyst_relations",
        event: "analyst.briefing_prep",
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to proactively identify industry analysts whose coverage areas align with upcoming product announcements and company milestones, then auto-generates and sends personalized briefing requests",
        setup:
          "Set OpenAI API key for analyst identification and briefing generation. Configure analyst contact resolution API (e.g., Hunter.io). Set email validation API credentials. Configure SMTP for outreach. Set Google Sheets credentials for analyst-relations tracking.",
        inputFields: [
          {
            name: "product_area",
            type: "string",
            example: "AI-powered sales engagement",
          },
          {
            name: "announcement_topic",
            type: "string",
            example: "Q2 product launch: predictive pipeline scoring",
          },
          { name: "company_name", type: "string", example: "OmniEngage" },
        ],
      }),

      // 2. AI analyst identification
      aiNode(
        "2",
        "AI Identify Relevant Analysts",
        280,
        "You are an expert analyst relations strategist. Given a product area and upcoming announcement topic, identify the most relevant industry analysts and research firms who cover this space. Consider Gartner, Forrester, IDC, G2, and niche analysts. Respond JSON: {analysts: string, top_analyst_name: string, top_analyst_firm: string, top_analyst_beat: string, coverage_relevance: string, briefing_priority: string, total_identified: number}",
        "Product area: {product_area}. Announcement: {announcement_topic}. Company: {company_name}. Identify the top analysts covering this space and rank by coverage relevance.",
        [
          { field: "analysts", as: "analysts_list", type: "string" },
          { field: "top_analyst_name", as: "analyst_name", type: "string" },
          { field: "top_analyst_firm", as: "analyst_firm", type: "string" },
          { field: "top_analyst_beat", as: "analyst_beat", type: "string" },
          {
            field: "coverage_relevance",
            as: "coverage_relevance",
            type: "string",
          },
          {
            field: "briefing_priority",
            as: "briefing_priority",
            type: "string",
          },
          { field: "total_identified", as: "total_analysts", type: "number" },
        ],
      ),

      // 3. AI coverage alignment analysis
      aiNode(
        "3",
        "AI Analyze Coverage Alignment",
        520,
        "You are an analyst relations expert. Given the top analyst's beat, firm, and coverage relevance, plus the company's announcement topic, craft a coverage alignment analysis. Identify which specific research notes, Magic Quadrant positions, or Wave reports this analyst has published that relate to the company's space. Suggest talking points. Respond JSON: {alignment_score: number, key_research_cited: string, talking_points: string, competitor_mentions: string, positioning_angle: string, briefing_format: string}",
        "Analyst: {analyst_name} at {analyst_firm}. Beat: {analyst_beat}. Coverage relevance: {coverage_relevance}. Announcement: {announcement_topic}. Product area: {product_area}. Analyze alignment and build talking points.",
        [
          { field: "alignment_score", as: "alignment_score", type: "number" },
          {
            field: "key_research_cited",
            as: "key_research_cited",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          {
            field: "competitor_mentions",
            as: "competitor_mentions",
            type: "string",
          },
          {
            field: "positioning_angle",
            as: "positioning_angle",
            type: "string",
          },
          { field: "briefing_format", as: "briefing_format", type: "string" },
        ],
      ),

      // 4. AI briefing-request generation
      aiNode(
        "4",
        "AI Generate Briefing Request Email",
        760,
        "You are a senior analyst relations manager. Write a professional briefing request email to an industry analyst. The tone should be respectful and concise — analysts are busy. Reference their specific research, explain why a briefing would be mutually valuable, and propose 2-3 time slots. Keep under 200 words. Respond JSON: {subject: string, body: string, call_to_action: string, proposed_duration: string}",
        "Analyst: {analyst_name} at {analyst_firm}. Beat: {analyst_beat}. Key research: {key_research_cited}. Talking points: {talking_points}. Positioning angle: {positioning_angle}. Company: {company_name}. Announcement: {announcement_topic}. Format: {briefing_format}. Write the briefing request.",
        [
          { field: "subject", as: "briefing_subject", type: "string" },
          { field: "body", as: "briefing_body", type: "string" },
          { field: "call_to_action", as: "briefing_cta", type: "string" },
          {
            field: "proposed_duration",
            as: "proposed_duration",
            type: "string",
          },
        ],
      ),

      // 5. Contact process resolution API
      n("5", "hitApi", "Resolve Analyst Contact via Hunter.io", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={analyst_firm}&first_name={analyst_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "data.email", customName: "analyst_email", type: "string" },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
            {
              name: "data.first_name",
              customName: "analyst_first_name",
              type: "string",
            },
            {
              name: "data.last_name",
              customName: "analyst_last_name",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Analyst Email Address", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={analyst_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Briefing request send + analyst-relations tracker Sheets
      emailNode(
        "7",
        "Send Analyst Briefing Request",
        1480,
        "{analyst_email}",
        "{briefing_subject}",
        "<p>Dear {analyst_first_name},</p><p>{briefing_body}</p><p>{briefing_cta}</p><p>Proposed duration: {proposed_duration}</p><p>Best regards,<br/>{company_name} Analyst Relations</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1809. INFLUENCER PARTNERSHIP OUTREACH (8 nodes)
  // ===================================================================

  "Influencer Partnership Outreach": {
    summary:
      "Runs on schedule to discover relevant influencers with AI, analyze their profiles, score brand alignment with runCode, match collaboration opportunities with runCode, resolve contact info via API, validate email addresses, generate personalized partnership pitches with AI, and log the pipeline to HubSpot.",
    nodes: [
      trigger("Schedule: Weekly influencer discovery", "schedule", {
        source: "partnerships",
        event: "influencer.discovery",
        schedule: "Every Wednesday at 10:00 AM",
        howItStarts:
          "Runs weekly to discover and vet influencers whose audience demographics and content style align with the brand, then auto-generates partnership proposals and tracks the pipeline",
        setup:
          "Set OpenAI API key for influencer discovery and pitch generation. Configure contact discovery API (Hunter.io or Apollo). Set email validation API credentials. Provide HubSpot token for pipeline tracking.",
        inputFields: [
          { name: "brand_name", type: "string", example: "OmniEngage" },
          {
            name: "target_niche",
            type: "string",
            example: "B2B SaaS marketing and sales technology",
          },
          {
            name: "budget_range",
            type: "string",
            example: "$2,000-$10,000 per partnership",
          },
          {
            name: "campaign_goal",
            type: "string",
            example: "Drive awareness for AI workflow automation",
          },
        ],
      }),

      // 2. AI influencer discovery
      aiNode(
        "2",
        "AI Discover Relevant Influencers",
        280,
        "You are an influencer marketing strategist. Given a brand, target niche, and campaign goal, identify the top influencer profiles across LinkedIn, YouTube, Twitter/X, and industry blogs. For each, note their platform, follower count estimate, content style, and audience relevance. Respond JSON: {top_influencer_name: string, platform: string, follower_estimate: string, content_style: string, audience_relevance: string, engagement_quality: string, niche_authority: string, influencer_domain: string}",
        "Brand: {brand_name}. Niche: {target_niche}. Campaign goal: {campaign_goal}. Budget: {budget_range}. Find the best-fit influencer for a partnership.",
        [
          {
            field: "top_influencer_name",
            as: "influencer_name",
            type: "string",
          },
          { field: "platform", as: "influencer_platform", type: "string" },
          {
            field: "follower_estimate",
            as: "follower_estimate",
            type: "string",
          },
          { field: "content_style", as: "content_style", type: "string" },
          {
            field: "audience_relevance",
            as: "audience_relevance",
            type: "string",
          },
          {
            field: "engagement_quality",
            as: "engagement_quality",
            type: "string",
          },
          { field: "niche_authority", as: "niche_authority", type: "string" },
          {
            field: "influencer_domain",
            as: "influencer_domain",
            type: "string",
          },
        ],
      ),

      // 3. AI profile analysis
      aiNode(
        "3",
        "AI Analyze Influencer Profile",
        520,
        "You are an influencer vetting specialist. Given the discovered influencer's platform, content style, audience relevance, and engagement quality, perform a deep analysis. Evaluate authenticity signals, brand-safety concerns, audience overlap with target demographics, previous brand partnerships, and content production quality. Respond JSON: {authenticity_score: number, brand_safety_rating: string, audience_overlap_pct: string, past_partnerships: string, content_quality: string, risk_flags: string, recommendation: string}",
        "Influencer: {influencer_name} on {influencer_platform}. Followers: {follower_estimate}. Content style: {content_style}. Audience relevance: {audience_relevance}. Engagement: {engagement_quality}. Authority: {niche_authority}. Analyze their profile for brand partnership.",
        [
          {
            field: "authenticity_score",
            as: "authenticity_score",
            type: "number",
          },
          {
            field: "brand_safety_rating",
            as: "brand_safety_rating",
            type: "string",
          },
          {
            field: "audience_overlap_pct",
            as: "audience_overlap_pct",
            type: "string",
          },
          {
            field: "past_partnerships",
            as: "past_partnerships",
            type: "string",
          },
          { field: "content_quality", as: "content_quality", type: "string" },
          { field: "risk_flags", as: "risk_flags", type: "string" },
          {
            field: "recommendation",
            as: "profile_recommendation",
            type: "string",
          },
        ],
      ),

      // 4. Brand-alignment scoring runCode
      n("4", "runCode", "Score Brand Alignment", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score brand alignment based on profile analysis
const authenticity = parseInt(vars.authenticity_score) || 0;
const safety = (vars.brand_safety_rating || "").toLowerCase();
const overlap = parseInt(vars.audience_overlap_pct) || 0;
const quality = (vars.content_quality || "").toLowerCase();
const risks = (vars.risk_flags || "none").toLowerCase();
let alignment_score = 0;
// Authenticity (0-30 points)
alignment_score += Math.min(30, Math.round(authenticity * 0.3));
// Brand safety (0-25 points)
if (safety === "excellent" || safety === "high") alignment_score += 25;
else if (safety === "good" || safety === "medium") alignment_score += 15;
else alignment_score += 5;
// Audience overlap (0-25 points)
alignment_score += Math.min(25, Math.round(overlap * 0.25));
// Content quality (0-20 points)
if (quality.includes("excellent") || quality.includes("high")) alignment_score += 20;
else if (quality.includes("good")) alignment_score += 12;
else alignment_score += 5;
// Risk deduction
if (risks !== "none" && risks !== "") alignment_score -= 10;
const final_score = Math.max(0, Math.min(100, alignment_score));
const tier = final_score >= 80 ? "premium" : final_score >= 60 ? "standard" : "exploratory";
return { alignment_score: final_score, partnership_tier: tier, passes_threshold: final_score >= 50 ? "true" : "false" };`,
          outputVariables: [
            { name: "alignment_score", type: "number" },
            { name: "partnership_tier", type: "string" },
            { name: "passes_threshold", type: "string" },
          ],
        },
      }),

      // 5. Collaboration matching runCode
      n("5", "runCode", "Match Collaboration Format", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match collaboration format based on platform, tier, and budget
const platform = (vars.influencer_platform || "").toLowerCase();
const tier = vars.partnership_tier || "standard";
const budget = vars.budget_range || "$2,000-$10,000";
let collab_type = "";
let deliverables = "";
let timeline = "";
if (platform.includes("youtube")) {
  collab_type = tier === "premium" ? "dedicated_video" : "integration_mention";
  deliverables = tier === "premium" ? "1 dedicated video (8-15 min) + 2 social posts" : "1 integrated mention (60-90 sec) + 1 social post";
  timeline = "4-6 weeks production";
} else if (platform.includes("linkedin")) {
  collab_type = tier === "premium" ? "thought_leadership_series" : "sponsored_post";
  deliverables = tier === "premium" ? "3 LinkedIn articles + 1 webinar co-host" : "2 sponsored posts + 1 story mention";
  timeline = "2-3 weeks production";
} else if (platform.includes("twitter") || platform.includes("x")) {
  collab_type = tier === "premium" ? "campaign_ambassador" : "tweet_series";
  deliverables = tier === "premium" ? "10 tweets + 2 spaces appearances over 30 days" : "5 tweets + 1 thread over 14 days";
  timeline = "2-4 weeks";
} else {
  collab_type = "content_partnership";
  deliverables = "2 blog posts + 3 social shares";
  timeline = "3-4 weeks production";
}
return { collaboration_type: collab_type, deliverables: deliverables, timeline: timeline, proposed_budget: budget };`,
          outputVariables: [
            { name: "collaboration_type", type: "string" },
            { name: "deliverables", type: "string" },
            { name: "timeline", type: "string" },
            { name: "proposed_budget", type: "string" },
          ],
        },
      }),

      // 6. Contact resolution API
      n("6", "hitApi", "Discover Influencer Contact via Hunter.io", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={influencer_domain}&first_name={influencer_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.email",
              customName: "influencer_email",
              type: "string",
            },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 7. Email validation API
      n("7", "hitApi", "Validate Influencer Email", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={influencer_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. AI partnership pitch + pipeline HubSpot
      aiNode(
        "8",
        "AI Generate Partnership Pitch",
        1720,
        "You are an influencer partnerships manager. Write a compelling, personalized partnership proposal email. Reference the influencer's content style, audience, and specific posts. Present the collaboration format, deliverables, timeline, and budget range. Keep professional but enthusiastic. Under 200 words. Respond JSON: {pitch_subject: string, pitch_body: string}",
        "Influencer: {influencer_name} on {influencer_platform}. Content style: {content_style}. Audience relevance: {audience_relevance}. Alignment score: {alignment_score}/100. Tier: {partnership_tier}. Collaboration: {collaboration_type}. Deliverables: {deliverables}. Timeline: {timeline}. Budget: {proposed_budget}. Brand: {brand_name}. Goal: {campaign_goal}. Write the partnership pitch.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1810. INTEGRATION PARTNER OUTREACH (8 nodes)
  // ===================================================================

  "Integration Partner Outreach": {
    summary:
      "Runs on schedule to identify integration partner candidates with AI, assess technical compatibility with runCode, quantify partnership value with runCode, discover partner contacts via API, validate email addresses, generate a technical proposal with AI, send the proposal, and log the pipeline to HubSpot with a Slack alert.",
    nodes: [
      trigger("Schedule: Bi-weekly integration partner scouting", "schedule", {
        source: "partnerships",
        event: "integration.partner_scout",
        schedule: "Every other Monday at 10:00 AM",
        howItStarts:
          "Runs bi-weekly to identify SaaS companies whose products complement the platform, assess technical integration feasibility, and send personalized partnership proposals",
        setup:
          "Set OpenAI API key for candidate identification and proposal generation. Configure partner contact discovery API. Set email validation API credentials. Provide HubSpot token for pipeline tracking. Configure Slack webhook for alerts.",
        inputFields: [
          { name: "platform_name", type: "string", example: "OmniEngage" },
          {
            name: "platform_category",
            type: "string",
            example: "Sales engagement and workflow automation",
          },
          {
            name: "integration_priorities",
            type: "string",
            example: "CRM, email providers, analytics, payment processing",
          },
          {
            name: "api_type",
            type: "string",
            example: "REST API with webhook support",
          },
        ],
      }),

      // 2. AI candidate identification
      aiNode(
        "2",
        "AI Identify Integration Candidates",
        280,
        "You are a strategic partnerships analyst. Given a platform's category and integration priorities, identify the best potential integration partner companies. Consider market overlap, customer base complementarity, technical compatibility, and strategic value. Respond JSON: {partner_company: string, partner_domain: string, partner_category: string, integration_type: string, customer_overlap_estimate: string, strategic_value: string, api_availability: string, market_position: string}",
        "Platform: {platform_name}. Category: {platform_category}. Integration priorities: {integration_priorities}. API type: {api_type}. Identify the top integration partner candidate.",
        [
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "partner_domain", as: "partner_domain", type: "string" },
          { field: "partner_category", as: "partner_category", type: "string" },
          { field: "integration_type", as: "integration_type", type: "string" },
          {
            field: "customer_overlap_estimate",
            as: "customer_overlap",
            type: "string",
          },
          { field: "strategic_value", as: "strategic_value", type: "string" },
          { field: "api_availability", as: "api_availability", type: "string" },
          { field: "market_position", as: "market_position", type: "string" },
        ],
      ),

      // 3. Technical assessment runCode
      n("3", "runCode", "Assess Technical Compatibility", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assess technical compatibility for integration
const apiType = (vars.api_type || "").toLowerCase();
const partnerApi = (vars.api_availability || "").toLowerCase();
const integrationType = (vars.integration_type || "").toLowerCase();
let tech_score = 50;
// API compatibility
if (partnerApi.includes("rest") && apiType.includes("rest")) tech_score += 20;
else if (partnerApi.includes("graphql") || partnerApi.includes("rest")) tech_score += 10;
if (partnerApi.includes("webhook")) tech_score += 10;
// Integration complexity
let complexity = "medium";
let estimated_weeks = 6;
if (integrationType.includes("data sync") || integrationType.includes("bidirectional")) {
  complexity = "high";
  estimated_weeks = 10;
} else if (integrationType.includes("webhook") || integrationType.includes("event")) {
  complexity = "low";
  estimated_weeks = 3;
}
// Documentation availability
if (partnerApi.includes("documented") || partnerApi.includes("public")) tech_score += 10;
const final_score = Math.min(100, tech_score);
return { tech_compatibility_score: final_score, complexity: complexity, estimated_dev_weeks: estimated_weeks, feasibility: final_score >= 60 ? "feasible" : "needs_investigation" };`,
          outputVariables: [
            { name: "tech_compatibility_score", type: "number" },
            { name: "complexity", type: "string" },
            { name: "estimated_dev_weeks", type: "number" },
            { name: "feasibility", type: "string" },
          ],
        },
      }),

      // 4. Value quantification runCode
      n("4", "runCode", "Quantify Partnership Value", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Quantify the business value of the integration partnership
const overlap = (vars.customer_overlap || "").toLowerCase();
const strategic = (vars.strategic_value || "").toLowerCase();
const position = (vars.market_position || "").toLowerCase();
const devWeeks = parseInt(vars.estimated_dev_weeks) || 6;
let value_score = 50;
// Customer overlap value
if (overlap.includes("high") || overlap.includes("significant")) value_score += 25;
else if (overlap.includes("moderate") || overlap.includes("medium")) value_score += 15;
else value_score += 5;
// Strategic importance
if (strategic.includes("critical") || strategic.includes("high")) value_score += 20;
else if (strategic.includes("moderate")) value_score += 10;
// Market position premium
if (position.includes("leader") || position.includes("dominant")) value_score += 15;
else if (position.includes("growing") || position.includes("challenger")) value_score += 10;
// ROI estimation
const devCost = devWeeks * 8000; // estimated weekly dev cost
const annualRevUpside = value_score >= 80 ? 250000 : value_score >= 60 ? 120000 : 50000;
const roi_months = Math.ceil((devCost / (annualRevUpside / 12)));
return { partnership_value_score: Math.min(100, value_score), estimated_dev_cost: devCost, annual_revenue_upside: annualRevUpside, roi_payback_months: roi_months, priority: value_score >= 75 ? "high" : value_score >= 50 ? "medium" : "low" };`,
          outputVariables: [
            { name: "partnership_value_score", type: "number" },
            { name: "estimated_dev_cost", type: "number" },
            { name: "annual_revenue_upside", type: "number" },
            { name: "roi_payback_months", type: "number" },
            { name: "priority", type: "string" },
          ],
        },
      }),

      // 5. Partner contact discovery API
      n("5", "hitApi", "Discover Partner Contact via Apollo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{partner_company}" },
            {
              key: "title",
              value: "Head of Partnerships OR VP Business Development",
            },
          ],
          variables: [
            {
              name: "person.email",
              customName: "partner_contact_email",
              type: "string",
            },
            {
              name: "person.first_name",
              customName: "partner_first_name",
              type: "string",
            },
            {
              name: "person.last_name",
              customName: "partner_last_name",
              type: "string",
            },
            {
              name: "person.title",
              customName: "partner_contact_title",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Partner Contact Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={partner_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI technical proposal
      aiNode(
        "7",
        "AI Draft Technical Partnership Proposal",
        1480,
        "You are a partnerships director. Write a concise technical partnership proposal email to a potential integration partner. Reference their product category, explain the mutual customer value, outline the proposed integration type and estimated timeline, and include business value projections. Professional, specific, under 250 words. Respond JSON: {proposal_subject: string, proposal_body: string}",
        "To: {partner_first_name} {partner_last_name} ({partner_contact_title}) at {partner_company} ({partner_category}). From: {platform_name} ({platform_category}). Integration type: {integration_type}. Complexity: {complexity}. Est. dev weeks: {estimated_dev_weeks}. Customer overlap: {customer_overlap}. Annual revenue upside: ${annual_revenue_upside}. ROI payback: {roi_payback_months} months. Tech score: {tech_compatibility_score}/100. Write the proposal.",
        [
          { field: "proposal_subject", as: "proposal_subject", type: "string" },
          { field: "proposal_body", as: "proposal_body", type: "string" },
        ],
      ),

      // 8. Proposal send + pipeline HubSpot + Slack alert
      hubspotPostNode(
        "8",
        "Log Partnership to HubSpot Pipeline",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Integration: {platform_name} x {partner_company}","pipeline":"partnerships","dealstage":"proposal_sent","amount":"{annual_revenue_upside}","partner_company":"{partner_company}","partner_contact":"{partner_contact_email}","integration_type":"{integration_type}","tech_score":"{tech_compatibility_score}","value_score":"{partnership_value_score}","priority":"{priority}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1811. INTERCOM TICKET ENRICHMENT (8 nodes)
  // ===================================================================

  "Intercom Ticket Enrichment": {
    summary:
      "Fires on Intercom ticket webhook events, pulls account data from HubSpot, runs AI sentiment analysis, classifies signal importance via condition, routes signals through a second condition gate, generates AI response recommendations, logs the signal to HubSpot CRM, and alerts stakeholders on Slack.",
    nodes: [
      trigger("Webhook: Intercom ticket event", "webhook_app", {
        source: "intercom",
        event: "conversation.created",
        howItStarts:
          "Fires when a new Intercom conversation or ticket is created — including the raw ticket data, customer info, and message body — to enrich, classify, and route the signal to the right team",
        setup:
          "Configure Intercom webhook for conversation.created events. Set OpenAI API key for sentiment analysis and response recommendation. Provide HubSpot token for account data and signal logging. Configure Slack webhook for stakeholder alerts.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook payload
      aiNode(
        "2",
        "AI Extract Ticket Data",
        280,
        "You are a support data extraction specialist. Parse the raw Intercom webhook payload to extract ticket details regardless of format. Find the customer email, customer name, company name if available, message subject or topic, message body, conversation ID, priority or tag labels, and source channel. Respond JSON: {customer_email: string, customer_name: string, company_name: string, ticket_subject: string, ticket_body: string, conversation_id: string, priority_tags: string, source_channel: string}",
        "Raw Intercom webhook payload: {webhook_payload}. Extract all ticket details regardless of field naming conventions.",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "company_name", as: "ticket_company", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          { field: "ticket_body", as: "ticket_body", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "priority_tags", as: "priority_tags", type: "string" },
          { field: "source_channel", as: "source_channel", type: "string" },
        ],
      ),

      // 3. Account data pull HubSpot
      hubspotGetNode(
        "3",
        "Pull Account Data from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].properties.company",
            customName: "hs_company",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "owner_id",
            type: "string",
          },
        ],
      ),

      // 4. AI sentiment analysis
      aiNode(
        "4",
        "AI Analyze Ticket Sentiment",
        760,
        "You are a customer experience analyst. Analyze the support ticket for sentiment, urgency, churn risk, upsell opportunity, and underlying intent. Consider the customer's lifecycle stage and lead status for context. Respond JSON: {sentiment: string, urgency_level: string, churn_risk_score: number, upsell_signal: string, customer_intent: string, escalation_needed: string, signal_type: string}",
        "Ticket subject: {ticket_subject}. Body: {ticket_body}. Customer: {customer_name} at {ticket_company}. Lifecycle: {lifecycle_stage}. Lead status: {lead_status}. Priority tags: {priority_tags}. Channel: {source_channel}. Analyze sentiment and business signals.",
        [
          { field: "sentiment", as: "ticket_sentiment", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "churn_risk_score", as: "churn_risk_score", type: "number" },
          { field: "upsell_signal", as: "upsell_signal", type: "string" },
          { field: "customer_intent", as: "customer_intent", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
          { field: "signal_type", as: "signal_type", type: "string" },
        ],
      ),

      // 5. Signal classification condition
      n("5", "condition", "Is Escalation Needed?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "escalation_needed", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Signal routing gate condition
      n("6", "condition", "Is Churn Risk High?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "churn_risk_score", operator: "gt", value: "70" },
          ],
        },
      }),

      // 7. AI response recommendation
      aiNode(
        "7",
        "AI Generate Response Recommendation",
        1480,
        "You are a senior customer success strategist. Based on the ticket sentiment, urgency, churn risk, and customer intent, generate a recommended response approach. Include the suggested tone, key points to address, proactive offers to make, and escalation path if needed. Respond JSON: {recommended_response: string, suggested_tone: string, key_points: string, proactive_offer: string, escalation_path: string, follow_up_timeline: string}",
        "Ticket: {ticket_subject} — {ticket_body}. Sentiment: {ticket_sentiment}. Urgency: {urgency_level}. Churn risk: {churn_risk_score}/100. Upsell signal: {upsell_signal}. Intent: {customer_intent}. Signal type: {signal_type}. Lifecycle: {lifecycle_stage}. Generate the response strategy.",
        [
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
          { field: "suggested_tone", as: "suggested_tone", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "proactive_offer", as: "proactive_offer", type: "string" },
          { field: "escalation_path", as: "escalation_path", type: "string" },
          {
            field: "follow_up_timeline",
            as: "follow_up_timeline",
            type: "string",
          },
        ],
      ),

      // 8. Stakeholder alert Slack
      slackNode(
        "8",
        "Alert Stakeholders on Slack",
        1720,
        ":intercom: *Intercom Ticket Enriched*\n*Customer:* {customer_name} ({ticket_company})\n*Subject:* {ticket_subject}\n*Sentiment:* {ticket_sentiment} | *Urgency:* {urgency_level}\n*Churn Risk:* {churn_risk_score}/100 | *Signal:* {signal_type}\n*Upsell:* {upsell_signal}\n*Recommended Response:* {recommended_response}\n*Tone:* {suggested_tone}\n*Follow-up:* {follow_up_timeline}\n*Conversation:* {conversation_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1812. INVESTOR RELATIONS UPDATE (7 nodes)
  // ===================================================================

  "Investor Relations Update": {
    summary:
      "Runs on a monthly schedule to aggregate company metrics with runCode, compile milestones with runCode, segment investors with AI, generate personalized updates with AI, assemble visual reports with runCode, and send investor emails with engagement tracking in Sheets.",
    nodes: [
      trigger("Schedule: Monthly investor update", "schedule", {
        source: "investor_relations",
        event: "investor.monthly_update",
        schedule: "First Monday of each month at 8:00 AM",
        howItStarts:
          "Runs monthly to compile company metrics, milestones, and progress updates, then generates personalized investor communications segmented by investor type and engagement level",
        setup:
          "Set OpenAI API key for investor segmentation and update generation. Configure company metrics API or data warehouse access. Set SMTP credentials for investor email delivery. Set Google Sheets credentials for engagement tracking.",
        inputFields: [
          { name: "company_name", type: "string", example: "OmniEngage" },
          { name: "reporting_period", type: "string", example: "March 2026" },
          { name: "arr_current", type: "string", example: "$4.2M" },
          { name: "mrr_growth_pct", type: "string", example: "12%" },
          { name: "total_customers", type: "string", example: "340" },
          { name: "runway_months", type: "string", example: "18" },
        ],
      }),

      // 2. Metrics aggregation runCode
      n("2", "runCode", "Aggregate Company Metrics", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate and format company metrics for investor update
const arr = vars.arr_current || "$0";
const mrrGrowth = vars.mrr_growth_pct || "0%";
const customers = parseInt(vars.total_customers) || 0;
const runway = parseInt(vars.runway_months) || 0;
const period = vars.reporting_period || "Current Month";
// Calculate derived metrics
const arrNumeric = parseFloat(arr.replace(/[$,M]/g, '')) * 1000000;
const mrrNumeric = Math.round(arrNumeric / 12);
const growthRate = parseFloat(mrrGrowth.replace('%', '')) / 100;
const projectedArr12m = Math.round(arrNumeric * Math.pow(1 + growthRate, 12));
const avgDealSize = customers > 0 ? Math.round(arrNumeric / customers) : 0;
const burnRate = runway > 0 ? Math.round(arrNumeric * 0.3 / runway) : 0;
return {
  metrics_summary: "ARR: " + arr + " | MRR Growth: " + mrrGrowth + " | Customers: " + customers + " | Runway: " + runway + "mo",
  mrr_formatted: "$" + mrrNumeric.toLocaleString(),
  projected_arr_12m: "$" + (projectedArr12m / 1000000).toFixed(1) + "M",
  avg_deal_size: "$" + avgDealSize.toLocaleString(),
  burn_rate_monthly: "$" + burnRate.toLocaleString(),
  health_indicator: growthRate >= 0.1 ? "strong_growth" : growthRate >= 0.05 ? "healthy" : "needs_attention"
};`,
          outputVariables: [
            { name: "metrics_summary", type: "string" },
            { name: "mrr_formatted", type: "string" },
            { name: "projected_arr_12m", type: "string" },
            { name: "avg_deal_size", type: "string" },
            { name: "burn_rate_monthly", type: "string" },
            { name: "health_indicator", type: "string" },
          ],
        },
      }),

      // 3. Milestone compilation runCode
      n("3", "runCode", "Compile Milestones and Highlights", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile milestones based on metrics trends
const customers = parseInt(vars.total_customers) || 0;
const growth = vars.mrr_growth_pct || "0%";
const growthNum = parseFloat(growth.replace('%', ''));
const health = vars.health_indicator || "healthy";
const milestones = [];
// Customer milestones
if (customers >= 300) milestones.push("Crossed 300 customer milestone");
if (customers >= 500) milestones.push("Surpassed 500 customers");
// Growth milestones
if (growthNum >= 15) milestones.push("Achieved " + growth + " MoM growth — above 15% benchmark");
if (growthNum >= 10) milestones.push("Sustained double-digit monthly growth at " + growth);
// Health-based highlights
if (health === "strong_growth") milestones.push("Metrics indicate strong growth trajectory");
const highlights = milestones.length > 0 ? milestones.join("; ") : "Steady progress across all key metrics";
const milestone_count = milestones.length;
return { milestones_text: highlights, milestone_count: milestone_count, growth_narrative: growthNum >= 10 ? "accelerating" : growthNum >= 5 ? "steady" : "stabilizing" };`,
          outputVariables: [
            { name: "milestones_text", type: "string" },
            { name: "milestone_count", type: "number" },
            { name: "growth_narrative", type: "string" },
          ],
        },
      }),

      // 4. AI investor segmentation
      aiNode(
        "4",
        "AI Segment Investors",
        760,
        "You are an investor relations specialist. Given the company's current metrics, milestones, and growth trajectory, determine the appropriate communication style for different investor segments: lead investors, board members, angel investors, and strategic partners. For each, specify the level of detail, key metrics to highlight, and tone. Respond JSON: {primary_segment: string, detail_level: string, key_metrics_to_highlight: string, tone: string, risk_disclosure_level: string, ask_or_no_ask: string}",
        "Company: {company_name}. Period: {reporting_period}. Metrics: {metrics_summary}. Projected ARR: {projected_arr_12m}. Milestones: {milestones_text}. Growth narrative: {growth_narrative}. Health: {health_indicator}. Segment the investor communication approach.",
        [
          { field: "primary_segment", as: "investor_segment", type: "string" },
          { field: "detail_level", as: "detail_level", type: "string" },
          {
            field: "key_metrics_to_highlight",
            as: "key_metrics_highlight",
            type: "string",
          },
          { field: "tone", as: "update_tone", type: "string" },
          {
            field: "risk_disclosure_level",
            as: "risk_disclosure",
            type: "string",
          },
          { field: "ask_or_no_ask", as: "include_ask", type: "string" },
        ],
      ),

      // 5. AI personalized update generation
      aiNode(
        "5",
        "AI Generate Investor Update Email",
        1000,
        "You are a CEO writing a monthly investor update. Write a professional, data-driven update email. Start with a one-line summary of the month, then cover: key metrics (with MoM comparisons), milestones achieved, product highlights, team updates, and outlook. If an ask is appropriate, include it naturally. Match the tone and detail level specified. Under 400 words. Respond JSON: {update_subject: string, update_body: string, closing_line: string}",
        "Company: {company_name}. Period: {reporting_period}. Metrics: {metrics_summary}. MRR: {mrr_formatted}. Projected ARR: {projected_arr_12m}. Avg deal: {avg_deal_size}. Burn: {burn_rate_monthly}/mo. Milestones: {milestones_text}. Growth: {growth_narrative}. Segment: {investor_segment}. Detail: {detail_level}. Highlight: {key_metrics_highlight}. Tone: {update_tone}. Risk level: {risk_disclosure}. Include ask: {include_ask}. Write the update.",
        [
          { field: "update_subject", as: "update_subject", type: "string" },
          { field: "update_body", as: "update_body", type: "string" },
          { field: "closing_line", as: "closing_line", type: "string" },
        ],
      ),

      // 6. Visual report assembly runCode
      n("6", "runCode", "Assemble Report Summary", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble a structured report summary for the email
const period = vars.reporting_period || "Current";
const metrics = vars.metrics_summary || "";
const milestones = vars.milestones_text || "";
const projected = vars.projected_arr_12m || "";
const health = vars.health_indicator || "";
const segment = vars.investor_segment || "";
const reportBlock = "--- " + period + " Investor Report ---\\n" +
  "Metrics: " + metrics + "\\n" +
  "12-Month Projection: " + projected + "\\n" +
  "Milestones: " + milestones + "\\n" +
  "Health: " + health + "\\n" +
  "Segment: " + segment;
const timestamp = new Date().toISOString();
return { report_block: reportBlock, generated_at: timestamp, report_format: "email_inline" };`,
          outputVariables: [
            { name: "report_block", type: "string" },
            { name: "generated_at", type: "string" },
            { name: "report_format", type: "string" },
          ],
        },
      }),

      // 7. Investor email send + engagement tracking Sheets
      sheetsNode(
        "7",
        "Log Investor Update to Sheets",
        1480,
        "InvestorUpdates",
        '[["{reporting_period}","{investor_segment}","{update_subject}","{metrics_summary}","{milestones_text}","{projected_arr_12m}","{health_indicator}","{generated_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1813. JOB POSTING PERSONALIZATION (7 nodes)
  // ===================================================================

  "Job Posting Personalization": {
    summary:
      "Runs on schedule to scrape target-company job postings via API, extract hiring signals with AI, build a growth narrative with runCode, map pain points with AI, generate a personalized outreach draft with AI, validate and send the email, and log to HubSpot CRM.",
    nodes: [
      trigger("Schedule: Daily job-posting scan", "schedule", {
        source: "sales_intelligence",
        event: "job.posting_scan",
        schedule: "Every weekday at 7:00 AM",
        howItStarts:
          "Runs daily to scan target company job postings for hiring signals that indicate product need, then auto-generates hyper-personalized outreach referencing specific job requirements and growth patterns",
        setup:
          "Set job scraping API credentials (e.g., SerpApi or similar). Set OpenAI API key for signal extraction and personalization. Configure email validation API. Set SMTP credentials. Provide HubSpot token for CRM logging.",
        inputFields: [
          { name: "target_company", type: "string", example: "Acme Corp" },
          { name: "target_domain", type: "string", example: "acmecorp.com" },
          {
            name: "product_category",
            type: "string",
            example: "sales automation",
          },
        ],
      }),

      // 2. Target-company job scrape API
      n("2", "hitApi", "Scrape Job Postings via SerpApi", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?engine=google_jobs&q={target_company}+{product_category}&api_key={serpapi_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "jobs_results[0].title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "jobs_results[0].company_name",
              customName: "posting_company",
              type: "string",
            },
            {
              name: "jobs_results[0].description",
              customName: "job_description",
              type: "string",
            },
            {
              name: "jobs_results[0].location",
              customName: "job_location",
              type: "string",
            },
            {
              name: "jobs_results[0].detected_extensions.posted_at",
              customName: "posted_date",
              type: "string",
            },
            {
              name: "jobs_results.length",
              customName: "total_postings",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI hiring-signal extraction
      aiNode(
        "3",
        "AI Extract Hiring Signals",
        520,
        "You are a sales intelligence analyst. Analyze the job posting to extract buying signals: what tools/technologies are mentioned, what problems they're solving, team size implications, budget indicators, timeline urgency, and what this hiring pattern suggests about their current pain points. Respond JSON: {tools_mentioned: string, problems_solving: string, team_growth_signal: string, budget_indicator: string, urgency_level: string, pain_points_implied: string, buying_signal_strength: string}",
        "Job title: {job_title} at {posting_company}. Description: {job_description}. Location: {job_location}. Posted: {posted_date}. Total postings found: {total_postings}. Our product category: {product_category}. Extract all hiring signals.",
        [
          { field: "tools_mentioned", as: "tools_mentioned", type: "string" },
          { field: "problems_solving", as: "problems_solving", type: "string" },
          {
            field: "team_growth_signal",
            as: "team_growth_signal",
            type: "string",
          },
          { field: "budget_indicator", as: "budget_indicator", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "pain_points_implied",
            as: "pain_points_implied",
            type: "string",
          },
          {
            field: "buying_signal_strength",
            as: "buying_signal_strength",
            type: "string",
          },
        ],
      ),

      // 4. Growth narrative construction runCode
      n("4", "runCode", "Build Growth Narrative", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a growth narrative from hiring signals
const totalPostings = parseInt(vars.total_postings) || 0;
const growth = (vars.team_growth_signal || "").toLowerCase();
const urgency = (vars.urgency_level || "").toLowerCase();
const budget = (vars.budget_indicator || "").toLowerCase();
let narrative = "";
if (totalPostings >= 5) {
  narrative = "aggressive_hiring";
} else if (totalPostings >= 2) {
  narrative = "team_expansion";
} else {
  narrative = "targeted_hire";
}
let growth_phase = "";
if (growth.includes("rapid") || growth.includes("scaling")) growth_phase = "hypergrowth";
else if (growth.includes("steady") || growth.includes("building")) growth_phase = "growth_stage";
else growth_phase = "established";
const timing_score = urgency.includes("high") || urgency.includes("urgent") ? 90 : urgency.includes("medium") ? 60 : 30;
return { growth_narrative: narrative, growth_phase: growth_phase, timing_score: timing_score, outreach_urgency: timing_score >= 70 ? "high" : "normal" };`,
          outputVariables: [
            { name: "growth_narrative", type: "string" },
            { name: "growth_phase", type: "string" },
            { name: "timing_score", type: "number" },
            { name: "outreach_urgency", type: "string" },
          ],
        },
      }),

      // 5. AI pain-point mapping
      aiNode(
        "5",
        "AI Map Pain Points to Product",
        1000,
        "You are a solution consultant. Given the hiring signals, implied pain points, and tools mentioned in the job posting, map these to specific product capabilities. Create a compelling connection between what they're hiring for and what the product solves. Respond JSON: {primary_pain_match: string, product_capability_fit: string, value_proposition: string, competitor_displacement: string, roi_angle: string}",
        "Company: {target_company}. Job: {job_title}. Pain points: {pain_points_implied}. Tools mentioned: {tools_mentioned}. Problems: {problems_solving}. Growth: {growth_narrative} ({growth_phase}). Budget: {budget_indicator}. Product: {product_category}. Map pain to product value.",
        [
          {
            field: "primary_pain_match",
            as: "primary_pain_match",
            type: "string",
          },
          {
            field: "product_capability_fit",
            as: "product_fit",
            type: "string",
          },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "competitor_displacement",
            as: "competitor_angle",
            type: "string",
          },
          { field: "roi_angle", as: "roi_angle", type: "string" },
        ],
      ),

      // 6. AI personalized draft
      aiNode(
        "6",
        "AI Draft Job-Signal Outreach Email",
        1240,
        "You are a top-performing SDR. Write a personalized cold email that references a specific job posting as the opening hook. Mention the exact job title and a specific requirement from the description. Connect it naturally to the product value proposition. Keep under 100 words. No fluff. Respond JSON: {email_subject: string, email_body: string}",
        "Company: {target_company}. Job: {job_title}. Posted: {posted_date}. Pain match: {primary_pain_match}. Product fit: {product_fit}. Value prop: {value_proposition}. ROI: {roi_angle}. Growth phase: {growth_phase}. Outreach urgency: {outreach_urgency}. Write the email referencing the job posting.",
        [
          { field: "email_subject", as: "outreach_subject", type: "string" },
          { field: "email_body", as: "outreach_body", type: "string" },
        ],
      ),

      // 7. Email validation + send + CRM log HubSpot
      hubspotPostNode(
        "7",
        "Log Job-Signal Lead to HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"company":"{target_company}","website":"{target_domain}","hs_lead_status":"NEW","lead_source":"job_posting_signal","job_title_spotted":"{job_title}","buying_signal_strength":"{buying_signal_strength}","growth_phase":"{growth_phase}","pain_match":"{primary_pain_match}","outreach_subject":"{outreach_subject}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1814. JOB-CHANGE TRIGGER OUTREACH (8 nodes)
  // ===================================================================

  "Job-Change Trigger Outreach": {
    summary:
      "Fires when a job-change event is detected via webhook, enriches the new role via Clearbit API, assesses the opportunity with AI, gates on classification via condition, drafts a congratulatory message with AI, validates the new domain email, sends the outreach, and updates CRM with a new opportunity in HubSpot.",
    nodes: [
      trigger("Webhook: Job change detected", "webhook_app", {
        source: "sales_intelligence",
        event: "contact.job_change",
        howItStarts:
          "Fires when a monitored contact changes jobs — detected via LinkedIn tracking, data provider alerts, or CRM enrichment — to trigger timely congratulatory outreach at the new company",
        setup:
          "Configure job-change detection webhook (e.g., from UserGems, Champify, or LinkedIn Sales Navigator). Set Clearbit API key for new-role enrichment. Set OpenAI API key for opportunity assessment and message drafting. Configure email validation API. Set SMTP and HubSpot credentials.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook
      aiNode(
        "2",
        "AI Extract Job-Change Details",
        280,
        "You are a sales intelligence data parser. Extract job-change details from the raw webhook payload regardless of format. Find the contact name, previous company, previous title, new company, new title, new company domain, LinkedIn profile URL if present, and change date. Respond JSON: {contact_name: string, previous_company: string, previous_title: string, new_company: string, new_title: string, new_domain: string, linkedin_url: string, change_date: string}",
        "Raw job-change webhook payload: {webhook_payload}. Extract all job-change details regardless of field naming.",
        [
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "previous_company", as: "previous_company", type: "string" },
          { field: "previous_title", as: "previous_title", type: "string" },
          { field: "new_company", as: "new_company", type: "string" },
          { field: "new_title", as: "new_title", type: "string" },
          { field: "new_domain", as: "new_domain", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "change_date", as: "change_date", type: "string" },
        ],
      ),

      // 3. New-role enrichment Clearbit API
      n("3", "hitApi", "Enrich New Company via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={new_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "new_company_name", type: "string" },
            {
              name: "description",
              customName: "new_company_description",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "new_company_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "new_company_size",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "new_company_revenue",
              type: "string",
            },
            { name: "tech", customName: "new_company_tech", type: "string" },
          ],
        },
      }),

      // 4. AI opportunity assessment
      aiNode(
        "4",
        "AI Assess Job-Change Opportunity",
        760,
        "You are a strategic sales analyst. Given a contact's job change — from their old role to their new role and company — assess the sales opportunity. Consider: does their new title give them buying authority? Is the new company a better ICP fit? Did they use our product at the old company (champion potential)? What's the best engagement approach? Respond JSON: {opportunity_score: number, buying_authority: string, icp_fit: string, champion_potential: string, engagement_approach: string, timing_recommendation: string, deal_size_estimate: string}",
        "Contact: {contact_name}. Previous: {previous_title} at {previous_company}. New: {new_title} at {new_company_name} ({new_company_industry}). Company size: {new_company_size}. Revenue: {new_company_revenue}. Tech: {new_company_tech}. Assess the opportunity.",
        [
          {
            field: "opportunity_score",
            as: "opportunity_score",
            type: "number",
          },
          { field: "buying_authority", as: "buying_authority", type: "string" },
          { field: "icp_fit", as: "icp_fit", type: "string" },
          {
            field: "champion_potential",
            as: "champion_potential",
            type: "string",
          },
          {
            field: "engagement_approach",
            as: "engagement_approach",
            type: "string",
          },
          {
            field: "timing_recommendation",
            as: "timing_recommendation",
            type: "string",
          },
          {
            field: "deal_size_estimate",
            as: "deal_size_estimate",
            type: "string",
          },
        ],
      ),

      // 5. Classification gate condition
      n("5", "condition", "Is Opportunity Score High Enough?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "opportunity_score", operator: "gt", value: "50" },
          ],
        },
      }),

      // 6. AI congrats draft
      aiNode(
        "6",
        "AI Draft Congratulations Message",
        1240,
        "You are a relationship-focused sales professional. Write a genuine congratulatory message for someone who just changed jobs. Reference their specific new role and company. Make it warm and personal — not salesy. If they're a former champion, reference the past relationship naturally. Include a soft bridge to reconnecting. Under 100 words. Respond JSON: {congrats_subject: string, congrats_body: string}",
        "Contact: {contact_name}. New role: {new_title} at {new_company_name}. Previous: {previous_title} at {previous_company}. Champion potential: {champion_potential}. Engagement approach: {engagement_approach}. Timing: {timing_recommendation}. Write the congratulations email.",
        [
          { field: "congrats_subject", as: "congrats_subject", type: "string" },
          { field: "congrats_body", as: "congrats_body", type: "string" },
        ],
      ),

      // 7. New-domain validation API + email send
      n("7", "hitApi", "Validate New Email Domain", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={new_domain}&first_name={contact_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "data.email", customName: "new_email", type: "string" },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 8. CRM update + new opportunity HubSpot
      hubspotPostNode(
        "8",
        "Create Job-Change Opportunity in HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Job Change: {contact_name} → {new_company_name}","pipeline":"default","dealstage":"appointmentscheduled","amount":"{deal_size_estimate}","contact_name":"{contact_name}","new_company":"{new_company_name}","new_title":"{new_title}","previous_company":"{previous_company}","opportunity_score":"{opportunity_score}","champion_potential":"{champion_potential}","icp_fit":"{icp_fit}","new_email":"{new_email}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1815. JOURNALIST PITCH OUTREACH (8 nodes)
  // ===================================================================

  "Journalist Pitch Outreach": {
    summary:
      "Runs on schedule to research reporter beats with AI, develop story angles with AI, personalize pitches with AI, verify contacts via API, validate emails via API, draft the final pitch with AI, send it, and log to a media tracker in Sheets.",
    nodes: [
      trigger("Schedule: Weekly media pitch cycle", "schedule", {
        source: "pr_communications",
        event: "media.pitch_cycle",
        schedule: "Every Tuesday at 9:00 AM",
        howItStarts:
          "Runs weekly to identify journalists covering relevant beats, develop story angles aligned with their interests, and send personalized media pitches for maximum coverage potential",
        setup:
          "Set OpenAI API key for reporter research, angle development, and pitch writing. Configure contact verification API (Hunter.io). Set email validation API credentials. Configure SMTP for pitch delivery. Set Google Sheets credentials for media tracking.",
        inputFields: [
          {
            name: "story_topic",
            type: "string",
            example:
              "AI-powered workflow automation reducing manual sales tasks by 80%",
          },
          { name: "company_name", type: "string", example: "OmniEngage" },
          {
            name: "news_hook",
            type: "string",
            example: "Series B funding announcement and product expansion",
          },
          {
            name: "target_publications",
            type: "string",
            example: "TechCrunch, VentureBeat, SaaStr, Forbes",
          },
        ],
      }),

      // 2. AI reporter beat research
      aiNode(
        "2",
        "AI Research Reporter Beats",
        280,
        "You are a PR strategist. Given target publications and a story topic, identify the most relevant journalist whose beat aligns with the story. Research their recent articles, coverage themes, writing style, and preferred pitch format. Respond JSON: {journalist_name: string, publication: string, beat_description: string, recent_articles: string, writing_style: string, preferred_pitch_format: string, journalist_domain: string, coverage_frequency: string}",
        "Story topic: {story_topic}. News hook: {news_hook}. Target publications: {target_publications}. Company: {company_name}. Find the best-fit journalist for this pitch.",
        [
          { field: "journalist_name", as: "journalist_name", type: "string" },
          { field: "publication", as: "publication", type: "string" },
          { field: "beat_description", as: "beat_description", type: "string" },
          { field: "recent_articles", as: "recent_articles", type: "string" },
          { field: "writing_style", as: "writing_style", type: "string" },
          {
            field: "preferred_pitch_format",
            as: "preferred_format",
            type: "string",
          },
          {
            field: "journalist_domain",
            as: "journalist_domain",
            type: "string",
          },
          {
            field: "coverage_frequency",
            as: "coverage_frequency",
            type: "string",
          },
        ],
      ),

      // 3. AI story-angle development
      aiNode(
        "3",
        "AI Develop Story Angle",
        520,
        "You are a media strategist. Given the journalist's beat, recent articles, and writing style, develop a story angle that aligns with their interests. The angle should be newsworthy, timely, and differentiated from generic product announcements. Include a data point or trend that makes it compelling. Respond JSON: {story_angle: string, headline_suggestion: string, data_hook: string, trend_connection: string, exclusivity_offer: string, why_now: string}",
        "Journalist: {journalist_name} at {publication}. Beat: {beat_description}. Recent: {recent_articles}. Style: {writing_style}. Story topic: {story_topic}. News hook: {news_hook}. Company: {company_name}. Develop a compelling angle.",
        [
          { field: "story_angle", as: "story_angle", type: "string" },
          {
            field: "headline_suggestion",
            as: "headline_suggestion",
            type: "string",
          },
          { field: "data_hook", as: "data_hook", type: "string" },
          { field: "trend_connection", as: "trend_connection", type: "string" },
          {
            field: "exclusivity_offer",
            as: "exclusivity_offer",
            type: "string",
          },
          { field: "why_now", as: "why_now", type: "string" },
        ],
      ),

      // 4. AI pitch personalization
      aiNode(
        "4",
        "AI Personalize Pitch Elements",
        760,
        "You are an expert PR pitch writer. Given the journalist's profile, story angle, and preferred format, personalize the pitch elements. Reference a specific recent article they wrote, connect it to the story angle, and craft a subject line that would catch their attention. Respond JSON: {personalized_opener: string, article_reference: string, connection_bridge: string, pitch_subject_line: string, pitch_tone: string}",
        "Journalist: {journalist_name}. Publication: {publication}. Beat: {beat_description}. Recent articles: {recent_articles}. Preferred format: {preferred_format}. Story angle: {story_angle}. Data hook: {data_hook}. Trend: {trend_connection}. Personalize the pitch elements.",
        [
          {
            field: "personalized_opener",
            as: "personalized_opener",
            type: "string",
          },
          {
            field: "article_reference",
            as: "article_reference",
            type: "string",
          },
          {
            field: "connection_bridge",
            as: "connection_bridge",
            type: "string",
          },
          { field: "pitch_subject_line", as: "pitch_subject", type: "string" },
          { field: "pitch_tone", as: "pitch_tone", type: "string" },
        ],
      ),

      // 5. Contact verification API
      n("5", "hitApi", "Verify Journalist Contact via Hunter.io", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={journalist_domain}&first_name={journalist_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.email",
              customName: "journalist_email",
              type: "string",
            },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Journalist Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={journalist_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI pitch draft
      aiNode(
        "7",
        "AI Draft Final Pitch Email",
        1480,
        "You are a top PR professional. Write the final pitch email to the journalist. Open with the personalized reference to their article, bridge to the story angle, present the data hook, explain why now, and close with a clear ask (interview, demo, exclusive). Keep under 200 words. No attachments mention. Respond JSON: {final_pitch_body: string}",
        "To: {journalist_name} at {publication}. Subject: {pitch_subject}. Opener: {personalized_opener}. Article ref: {article_reference}. Bridge: {connection_bridge}. Angle: {story_angle}. Data: {data_hook}. Why now: {why_now}. Exclusivity: {exclusivity_offer}. Tone: {pitch_tone}. Company: {company_name}. Write the final pitch.",
        [{ field: "final_pitch_body", as: "final_pitch_body", type: "string" }],
      ),

      // 8. Send + media tracker Sheets
      sheetsNode(
        "8",
        "Log Pitch to Media Tracker",
        1720,
        "MediaTracker",
        '[["{journalist_name}","{publication}","{beat_description}","{pitch_subject}","{story_angle}","{data_hook}","{email_confidence}","{email_status}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1816. KEY DECISION-MAKERS FROM URL (8 nodes)
  // ===================================================================

  "Key Decision-Makers from URL": {
    summary:
      "Fires on webhook with a company URL, resolves the domain and company with runCode, researches org structure with AI, discovers contacts via Hunter API, analyzes personas with AI, validates emails via API, prioritizes contacts with runCode, and pushes to HubSpot CRM with a Slack notification to sales.",
    nodes: [
      trigger("Webhook: Company URL submitted", "webhook_app", {
        source: "sales_tools",
        event: "url.submitted",
        howItStarts:
          "Fires when a sales rep submits a company URL to identify key decision-makers — extracting the domain, researching the org structure, and building a prioritized contact list with validated emails",
        setup:
          "Configure webhook to receive URL submissions from sales tools. Set OpenAI API key for org research and persona analysis. Configure Hunter.io API for contact discovery. Set email validation API credentials. Provide HubSpot token and Slack webhook.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook
      aiNode(
        "2",
        "AI Extract URL and Context",
        280,
        "You are a data extraction specialist. Parse the raw webhook payload to find the submitted company URL or domain, along with any additional context like the rep's name, target persona, or deal stage. Extract the clean domain from the URL. Respond JSON: {company_url: string, clean_domain: string, rep_name: string, target_persona: string, deal_context: string}",
        "Raw webhook payload: {webhook_payload}. Extract the company URL, clean domain, and any contextual details regardless of field naming.",
        [
          { field: "company_url", as: "company_url", type: "string" },
          { field: "clean_domain", as: "clean_domain", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "target_persona", as: "target_persona", type: "string" },
          { field: "deal_context", as: "deal_context", type: "string" },
        ],
      ),

      // 3. Domain company resolution runCode
      n("3", "runCode", "Resolve Domain to Company Details", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract clean domain and prepare company lookup parameters
const url = vars.company_url || vars.clean_domain || "";
let domain = url.replace(/^https?:\\/\\//, "").replace(/^www\\./, "").split("/")[0].toLowerCase();
// Remove port if present
domain = domain.split(":")[0];
// Validate domain format
const isValidDomain = domain.includes(".") && domain.length > 3;
const domainParts = domain.split(".");
const companyGuess = domainParts[0].charAt(0).toUpperCase() + domainParts[0].slice(1);
return {
  resolved_domain: domain,
  company_name_guess: companyGuess,
  is_valid_domain: isValidDomain ? "true" : "false",
  domain_tld: domainParts[domainParts.length - 1],
  lookup_ready: isValidDomain ? "true" : "false"
};`,
          outputVariables: [
            { name: "resolved_domain", type: "string" },
            { name: "company_name_guess", type: "string" },
            { name: "is_valid_domain", type: "string" },
            { name: "domain_tld", type: "string" },
            { name: "lookup_ready", type: "string" },
          ],
        },
      }),

      // 4. AI org-structure research
      aiNode(
        "4",
        "AI Research Org Structure",
        760,
        "You are an organizational intelligence analyst. Given a company domain and name guess, research the likely org structure. Identify the key departments and leadership roles that would be involved in purchasing decisions for the target persona's area. Map the typical decision-making unit: economic buyer, technical evaluator, champion, and blocker. Respond JSON: {company_confirmed: string, estimated_headcount: string, key_departments: string, economic_buyer_title: string, technical_evaluator_title: string, champion_title: string, org_complexity: string, decision_process: string}",
        "Domain: {resolved_domain}. Company guess: {company_name_guess}. Target persona: {target_persona}. Deal context: {deal_context}. Research the org structure and decision-making unit.",
        [
          {
            field: "company_confirmed",
            as: "company_confirmed",
            type: "string",
          },
          {
            field: "estimated_headcount",
            as: "estimated_headcount",
            type: "string",
          },
          { field: "key_departments", as: "key_departments", type: "string" },
          { field: "economic_buyer_title", as: "buyer_title", type: "string" },
          {
            field: "technical_evaluator_title",
            as: "evaluator_title",
            type: "string",
          },
          { field: "champion_title", as: "champion_title", type: "string" },
          { field: "org_complexity", as: "org_complexity", type: "string" },
          { field: "decision_process", as: "decision_process", type: "string" },
        ],
      ),

      // 5. Hunter contact discovery API
      n("5", "hitApi", "Discover Contacts via Hunter.io", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={resolved_domain}&api_key={hunter_api_key}&limit=10&type=personal",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "contact_1_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "contact_1_first",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "contact_1_last",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "contact_1_title",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "contact_1_confidence",
              type: "number",
            },
            {
              name: "data.emails[1].value",
              customName: "contact_2_email",
              type: "string",
            },
            {
              name: "data.emails[1].first_name",
              customName: "contact_2_first",
              type: "string",
            },
            {
              name: "data.emails[1].last_name",
              customName: "contact_2_last",
              type: "string",
            },
            {
              name: "data.emails[1].position",
              customName: "contact_2_title",
              type: "string",
            },
            {
              name: "meta.results",
              customName: "total_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 6. AI persona research
      aiNode(
        "6",
        "AI Analyze Contact Personas",
        1240,
        "You are a sales intelligence researcher. Given the discovered contacts and the org structure analysis, match each contact to a role in the decision-making unit (economic buyer, technical evaluator, champion, or influencer). Assess each contact's likely authority level and engagement recommendation. Respond JSON: {contact_1_role: string, contact_1_priority: string, contact_1_approach: string, contact_2_role: string, contact_2_priority: string, contact_2_approach: string, recommended_sequence: string, multi_thread_strategy: string}",
        "Contact 1: {contact_1_first} {contact_1_last}, {contact_1_title}. Contact 2: {contact_2_first} {contact_2_last}, {contact_2_title}. Company: {company_confirmed}. Headcount: {estimated_headcount}. Buyer title: {buyer_title}. Evaluator: {evaluator_title}. Champion: {champion_title}. Target persona: {target_persona}. Analyze and prioritize contacts.",
        [
          { field: "contact_1_role", as: "contact_1_role", type: "string" },
          {
            field: "contact_1_priority",
            as: "contact_1_priority",
            type: "string",
          },
          {
            field: "contact_1_approach",
            as: "contact_1_approach",
            type: "string",
          },
          { field: "contact_2_role", as: "contact_2_role", type: "string" },
          {
            field: "contact_2_priority",
            as: "contact_2_priority",
            type: "string",
          },
          {
            field: "contact_2_approach",
            as: "contact_2_approach",
            type: "string",
          },
          {
            field: "recommended_sequence",
            as: "recommended_sequence",
            type: "string",
          },
          {
            field: "multi_thread_strategy",
            as: "multi_thread_strategy",
            type: "string",
          },
        ],
      ),

      // 7. Validation waterfall + contact prioritization runCode
      n("7", "runCode", "Prioritize and Score Contacts", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prioritize contacts based on role, confidence, and approach
const c1Priority = (vars.contact_1_priority || "").toLowerCase();
const c2Priority = (vars.contact_2_priority || "").toLowerCase();
const c1Confidence = parseInt(vars.contact_1_confidence) || 0;
const c1Role = vars.contact_1_role || "unknown";
const c2Role = vars.contact_2_role || "unknown";
let c1Score = 0;
let c2Score = 0;
// Priority scoring
if (c1Priority === "high" || c1Priority === "1") c1Score += 40;
else if (c1Priority === "medium" || c1Priority === "2") c1Score += 25;
else c1Score += 10;
if (c2Priority === "high" || c2Priority === "1") c2Score += 40;
else if (c2Priority === "medium" || c2Priority === "2") c2Score += 25;
else c2Score += 10;
// Role scoring
const buyerRoles = ["economic_buyer", "buyer", "decision_maker"];
const champRoles = ["champion", "advocate", "internal_champion"];
if (buyerRoles.some(r => c1Role.toLowerCase().includes(r))) c1Score += 30;
if (champRoles.some(r => c1Role.toLowerCase().includes(r))) c1Score += 25;
if (buyerRoles.some(r => c2Role.toLowerCase().includes(r))) c2Score += 30;
if (champRoles.some(r => c2Role.toLowerCase().includes(r))) c2Score += 25;
// Confidence scoring
c1Score += Math.round(c1Confidence * 0.3);
const primaryContact = c1Score >= c2Score ? "contact_1" : "contact_2";
const primaryEmail = primaryContact === "contact_1" ? vars.contact_1_email : vars.contact_2_email;
const primaryName = primaryContact === "contact_1" ? vars.contact_1_first + " " + vars.contact_1_last : vars.contact_2_first + " " + vars.contact_2_last;
return { primary_contact: primaryContact, primary_email: primaryEmail, primary_name: primaryName, contact_1_score: c1Score, contact_2_score: c2Score, total_scored: 2 };`,
          outputVariables: [
            { name: "primary_contact", type: "string" },
            { name: "primary_email", type: "string" },
            { name: "primary_name", type: "string" },
            { name: "contact_1_score", type: "number" },
            { name: "contact_2_score", type: "number" },
            { name: "total_scored", type: "number" },
          ],
        },
      }),

      // 8. CRM push + sales notification HubSpot Slack
      slackNode(
        "8",
        "Notify Sales Rep with Decision-Makers",
        1720,
        ":dart: *Decision-Makers Identified for {company_confirmed}*\nRequested by: {rep_name}\n\n*Contact 1:* {contact_1_first} {contact_1_last} — {contact_1_title}\nRole: {contact_1_role} | Priority: {contact_1_priority} | Score: {contact_1_score}\nApproach: {contact_1_approach}\n\n*Contact 2:* {contact_2_first} {contact_2_last} — {contact_2_title}\nRole: {contact_2_role} | Priority: {contact_2_priority} | Score: {contact_2_score}\nApproach: {contact_2_approach}\n\n*Strategy:* {multi_thread_strategy}\n*Sequence:* {recommended_sequence}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1817. LEAD SOURCE NORMALIZATION (6 nodes)
  // ===================================================================

  "Lead Source Normalization": {
    summary:
      "Runs on schedule to extract lead sources from HubSpot, map them to a standard taxonomy with runCode, assess mapping confidence with runCode, route uncertain mappings through a human-review condition, batch-update CRM records, and generate an audit report in Sheets.",
    nodes: [
      trigger("Schedule: Weekly lead source cleanup", "schedule", {
        source: "revenue_ops",
        event: "lead_source.normalization",
        schedule: "Every Sunday at 6:00 AM",
        howItStarts:
          "Runs weekly to identify and normalize inconsistent lead source values across the CRM — mapping free-text entries, UTM variations, and legacy values to a standardized taxonomy for accurate attribution reporting",
        setup:
          "Provide HubSpot token for source extraction and batch updates. Set Google Sheets credentials for audit reporting.",
        inputFields: [
          { name: "taxonomy_version", type: "string", example: "v3.2" },
          { name: "batch_size", type: "string", example: "500" },
        ],
      }),

      // 2. Source extraction HubSpot
      hubspotGetNode(
        "2",
        "Extract Lead Sources from HubSpot",
        280,
        "crm/v3/objects/contacts?limit={batch_size}&properties=hs_analytics_source,hs_analytics_source_data_1,hs_analytics_source_data_2,utm_source,utm_medium,utm_campaign",
        [
          {
            name: "results.length",
            customName: "total_contacts",
            type: "number",
          },
          {
            name: "results[0].properties.hs_analytics_source",
            customName: "sample_source",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_source_data_1",
            customName: "sample_source_detail",
            type: "string",
          },
          {
            name: "results[0].properties.utm_source",
            customName: "sample_utm_source",
            type: "string",
          },
          {
            name: "results[0].properties.utm_medium",
            customName: "sample_utm_medium",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "sample_contact_id",
            type: "string",
          },
        ],
      ),

      // 3. Taxonomy mapping runCode
      n("3", "runCode", "Map Sources to Standard Taxonomy", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map raw lead sources to standardized taxonomy
const source = (vars.sample_source || "").toLowerCase().trim();
const detail = (vars.sample_source_detail || "").toLowerCase().trim();
const utmSource = (vars.sample_utm_source || "").toLowerCase().trim();
const utmMedium = (vars.sample_utm_medium || "").toLowerCase().trim();
const taxonomy = {
  "organic_search": ["organic search", "organic", "seo", "google", "bing", "search"],
  "paid_search": ["paid search", "ppc", "adwords", "google ads", "sem", "cpc"],
  "paid_social": ["paid social", "facebook ads", "linkedin ads", "twitter ads", "social ads"],
  "organic_social": ["organic social", "social media", "linkedin", "twitter", "facebook"],
  "email_marketing": ["email", "email marketing", "newsletter", "drip", "nurture"],
  "direct": ["direct", "direct traffic", "typed", "bookmark"],
  "referral": ["referral", "partner", "affiliate", "word of mouth"],
  "content_marketing": ["content", "blog", "webinar", "ebook", "whitepaper", "podcast"],
  "events": ["event", "conference", "tradeshow", "meetup", "virtual event"],
  "outbound": ["outbound", "cold email", "cold call", "sdr", "bdr", "sales"],
};
let normalized = "unclassified";
let confidence = 0;
const rawInput = source + " " + detail + " " + utmSource + " " + utmMedium;
for (const [category, keywords] of Object.entries(taxonomy)) {
  for (const keyword of keywords) {
    if (rawInput.includes(keyword)) {
      normalized = category;
      confidence = keyword === source ? 95 : keyword === utmSource ? 85 : 70;
      break;
    }
  }
  if (normalized !== "unclassified") break;
}
return { normalized_source: normalized, mapping_confidence: confidence, raw_source_combined: rawInput.trim(), taxonomy_category: normalized, needs_review: confidence < 70 ? "true" : "false" };`,
          outputVariables: [
            { name: "normalized_source", type: "string" },
            { name: "mapping_confidence", type: "number" },
            { name: "raw_source_combined", type: "string" },
            { name: "taxonomy_category", type: "string" },
            { name: "needs_review", type: "string" },
          ],
        },
      }),

      // 4. Mapping confidence runCode
      n("4", "runCode", "Assess Mapping Quality", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assess overall mapping quality and decide on batch action
const confidence = parseInt(vars.mapping_confidence) || 0;
const normalized = vars.normalized_source || "unclassified";
const needsReview = vars.needs_review || "false";
const totalContacts = parseInt(vars.total_contacts) || 0;
let action = "";
let review_reason = "";
if (confidence >= 85) {
  action = "auto_update";
  review_reason = "none";
} else if (confidence >= 60) {
  action = "auto_update_with_flag";
  review_reason = "moderate_confidence_flagged";
} else {
  action = "queue_for_review";
  review_reason = normalized === "unclassified" ? "unclassified_source" : "low_confidence_mapping";
}
const estimated_auto_updates = action === "queue_for_review" ? 0 : totalContacts;
return { update_action: action, review_reason: review_reason, estimated_updates: estimated_auto_updates, quality_tier: confidence >= 85 ? "high" : confidence >= 60 ? "medium" : "low", is_auto_approved: confidence >= 60 ? "true" : "false" };`,
          outputVariables: [
            { name: "update_action", type: "string" },
            { name: "review_reason", type: "string" },
            { name: "estimated_updates", type: "number" },
            { name: "quality_tier", type: "string" },
            { name: "is_auto_approved", type: "string" },
          ],
        },
      }),

      // 5. Human review queue condition
      n("5", "condition", "Is Auto-Approved for Update?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_auto_approved", operator: "eq", value: "true" },
          ],
        },
      }),

      // 6. CRM batch update + audit report HubSpot Sheets
      sheetsNode(
        "6",
        "Log Normalization Audit to Sheets",
        1240,
        "LeadSourceAudit",
        '[["{taxonomy_version}","{sample_contact_id}","{raw_source_combined}","{normalized_source}","{mapping_confidence}","{update_action}","{quality_tier}","{review_reason}","{estimated_updates}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
    ],
  },

  // ===================================================================
  // 1818. LEGAL STAKEHOLDER ENGAGEMENT (8 nodes)
  // ===================================================================

  "Legal Stakeholder Engagement": {
    summary:
      "Fires on webhook when a deal enters the legal stage, identifies legal contacts with runCode, prepares contract packages with AI, generates FAQ preemptions with runCode, assembles compliance docs with runCode, sends proactive legal outreach via email, logs legal tracking to HubSpot, and alerts on stalls via Slack escalation.",
    nodes: [
      trigger("Webhook: Deal enters legal stage", "webhook_app", {
        source: "crm",
        event: "deal.legal_stage_entered",
        howItStarts:
          "Fires when a deal moves to the legal/contract review stage in the CRM — triggering proactive engagement with the prospect's legal team to reduce contract cycle time and prevent stalls",
        setup:
          "Configure CRM webhook for deal stage changes to legal. Set OpenAI API key for contract prep and FAQ generation. Configure email sending credentials. Provide HubSpot token for legal tracking. Set Slack webhook for stall alerts.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook
      aiNode(
        "2",
        "AI Extract Deal Legal Details",
        280,
        "You are a deal data extraction specialist. Parse the raw webhook payload to find the deal name, deal amount, company name, primary contact name and email, deal stage, product/plan details, and any legal notes or requirements. Respond JSON: {deal_name: string, deal_amount: string, company_name: string, contact_name: string, contact_email: string, deal_id: string, product_plan: string, legal_notes: string, contract_type: string}",
        "Raw CRM webhook payload: {webhook_payload}. Extract all deal and legal details regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "company_name", as: "legal_company", type: "string" },
          { field: "contact_name", as: "legal_contact", type: "string" },
          { field: "contact_email", as: "legal_email", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "product_plan", as: "product_plan", type: "string" },
          { field: "legal_notes", as: "legal_notes", type: "string" },
          { field: "contract_type", as: "contract_type", type: "string" },
        ],
      ),

      // 3. Legal-contact identification runCode
      n("3", "runCode", "Identify Legal Contact Points", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify the right legal stakeholders based on deal size and contract type
const amount = parseFloat((vars.deal_amount || "0").replace(/[$,]/g, ""));
const contractType = (vars.contract_type || "standard").toLowerCase();
const notes = (vars.legal_notes || "").toLowerCase();
let legal_tier = "";
let required_docs = "";
let review_sla_days = 5;
let escalation_path = "";
if (amount >= 100000 || contractType.includes("enterprise") || contractType.includes("custom")) {
  legal_tier = "enterprise";
  required_docs = "MSA, DPA, SLA, SOC2 report, insurance certificate";
  review_sla_days = 10;
  escalation_path = "VP Legal → CLO";
} else if (amount >= 25000 || contractType.includes("professional")) {
  legal_tier = "professional";
  required_docs = "Standard contract, DPA, security questionnaire";
  review_sla_days = 7;
  escalation_path = "Legal Counsel → VP Legal";
} else {
  legal_tier = "standard";
  required_docs = "Standard terms, click-through DPA";
  review_sla_days = 3;
  escalation_path = "Legal Coordinator → Legal Counsel";
}
const has_special_requirements = notes.includes("gdpr") || notes.includes("hipaa") || notes.includes("sox") || notes.includes("fedramp");
if (has_special_requirements) review_sla_days += 3;
return { legal_tier: legal_tier, required_docs: required_docs, review_sla_days: review_sla_days, escalation_path: escalation_path, has_compliance_requirements: has_special_requirements ? "true" : "false" };`,
          outputVariables: [
            { name: "legal_tier", type: "string" },
            { name: "required_docs", type: "string" },
            { name: "review_sla_days", type: "number" },
            { name: "escalation_path", type: "string" },
            { name: "has_compliance_requirements", type: "string" },
          ],
        },
      }),

      // 4. AI contract-package prep
      aiNode(
        "4",
        "AI Prepare Contract Package",
        760,
        "You are a deal desk specialist. Given the deal details, legal tier, and required documents, prepare a contract package summary. Include the recommended contract template, key terms to highlight, common negotiation points for this tier, and a timeline recommendation. Respond JSON: {contract_template: string, key_terms_summary: string, negotiation_points: string, timeline_recommendation: string, package_cover_note: string, pricing_structure: string}",
        "Deal: {deal_name} for {legal_company}. Amount: {deal_amount}. Plan: {product_plan}. Contract type: {contract_type}. Legal tier: {legal_tier}. Required docs: {required_docs}. SLA: {review_sla_days} days. Compliance: {has_compliance_requirements}. Legal notes: {legal_notes}. Prepare the contract package.",
        [
          {
            field: "contract_template",
            as: "contract_template",
            type: "string",
          },
          { field: "key_terms_summary", as: "key_terms", type: "string" },
          {
            field: "negotiation_points",
            as: "negotiation_points",
            type: "string",
          },
          {
            field: "timeline_recommendation",
            as: "timeline_rec",
            type: "string",
          },
          { field: "package_cover_note", as: "cover_note", type: "string" },
          {
            field: "pricing_structure",
            as: "pricing_structure",
            type: "string",
          },
        ],
      ),

      // 5. FAQ preemption generation runCode
      n("5", "runCode", "Generate Legal FAQ Preemptions", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate FAQ preemptions based on legal tier and common objections
const tier = (vars.legal_tier || "standard").toLowerCase();
const hasCompliance = vars.has_compliance_requirements === "true";
const negotiationPts = vars.negotiation_points || "";
const faqs = [];
// Universal FAQs
faqs.push("Q: What is the contract term? A: Standard annual term with auto-renewal. Early termination with 30-day notice.");
faqs.push("Q: Is there a DPA available? A: Yes, our DPA is GDPR-compliant and available for immediate signature.");
if (tier === "enterprise") {
  faqs.push("Q: Can we modify the MSA? A: We support reasonable redlines. Our legal team typically turns around within 48 hours.");
  faqs.push("Q: Do you have SOC2 certification? A: Yes, SOC2 Type II. Report available under NDA.");
  faqs.push("Q: What about liability caps? A: Standard cap is 12 months of fees. Negotiable for enterprise tier.");
}
if (tier === "professional") {
  faqs.push("Q: Can we add custom SLA terms? A: Professional tier includes 99.9% uptime SLA with custom penalty terms available.");
}
if (hasCompliance) {
  faqs.push("Q: GDPR compliance? A: Full compliance including data residency options, DPO contact, and breach notification within 72 hours.");
  faqs.push("Q: Security certifications? A: SOC2 Type II, ISO 27001, and penetration testing reports available.");
}
return { faq_package: faqs.join("\\n\\n"), faq_count: faqs.length, preemption_coverage: tier === "enterprise" ? "comprehensive" : "standard" };`,
          outputVariables: [
            { name: "faq_package", type: "string" },
            { name: "faq_count", type: "number" },
            { name: "preemption_coverage", type: "string" },
          ],
        },
      }),

      // 6. Compliance-doc assembly runCode
      n("6", "runCode", "Assemble Compliance Document Links", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble links to required compliance documents
const requiredDocs = vars.required_docs || "";
const hasCompliance = vars.has_compliance_requirements === "true";
const docLinks = [];
if (requiredDocs.includes("MSA")) docLinks.push("MSA: https://docs.company.com/legal/msa-template");
if (requiredDocs.includes("DPA")) docLinks.push("DPA: https://docs.company.com/legal/dpa-v3");
if (requiredDocs.includes("SLA")) docLinks.push("SLA: https://docs.company.com/legal/sla-enterprise");
if (requiredDocs.includes("SOC2")) docLinks.push("SOC2 Report: https://docs.company.com/security/soc2-type2 (NDA required)");
if (requiredDocs.includes("insurance")) docLinks.push("Insurance Certificate: https://docs.company.com/legal/insurance-cert");
if (requiredDocs.includes("security")) docLinks.push("Security Questionnaire: https://docs.company.com/security/questionnaire-responses");
if (hasCompliance) {
  docLinks.push("GDPR Compliance Statement: https://docs.company.com/compliance/gdpr");
  docLinks.push("Privacy Policy: https://docs.company.com/legal/privacy");
}
return { doc_links_formatted: docLinks.join("\\n"), total_docs: docLinks.length, package_ready: docLinks.length > 0 ? "true" : "false" };`,
          outputVariables: [
            { name: "doc_links_formatted", type: "string" },
            { name: "total_docs", type: "number" },
            { name: "package_ready", type: "string" },
          ],
        },
      }),

      // 7. Proactive legal outreach email
      emailNode(
        "7",
        "Send Proactive Legal Outreach",
        1480,
        "{legal_email}",
        "Contract Package Ready: {deal_name} — {legal_company}",
        "<p>Hi {legal_contact},</p><p>{cover_note}</p><p><strong>Contract Details:</strong></p><p>Template: {contract_template}<br/>Key Terms: {key_terms}<br/>Pricing: {pricing_structure}</p><p><strong>Required Documents:</strong></p><p>{doc_links_formatted}</p><p><strong>Timeline:</strong> {timeline_rec} (SLA: {review_sla_days} business days)</p><p><strong>Common Questions:</strong></p><p>{faq_package}</p><p>Please don't hesitate to reach out with any questions. We're committed to making this process as smooth as possible.</p>",
      ),

      // 8. Stall alert + escalation Slack
      slackNode(
        "8",
        "Alert Legal Team on Slack",
        1720,
        ":scales: *Legal Stage Engagement Initiated*\n*Deal:* {deal_name} | *Amount:* {deal_amount}\n*Company:* {legal_company} | *Contact:* {legal_contact}\n*Legal Tier:* {legal_tier} | *SLA:* {review_sla_days} days\n*Contract:* {contract_template}\n*Docs Sent:* {total_docs} documents\n*FAQ Coverage:* {preemption_coverage} ({faq_count} FAQs)\n*Escalation Path:* {escalation_path}\n*Compliance Needs:* {has_compliance_requirements}\n:clock3: Follow up if no response by SLA deadline.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1819. LINK PROSPECT OUTREACH (7 nodes)
  // ===================================================================

  "Link Prospect Outreach": {
    summary:
      "Runs on schedule to qualify target sites with AI, analyze content matches with AI, develop a value proposition with AI, discover site owners via API, validate email addresses, draft outreach with AI, and log to a link tracker in Sheets.",
    nodes: [
      trigger("Schedule: Weekly link prospect scan", "schedule", {
        source: "seo_team",
        event: "link.prospect_scan",
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs weekly to identify high-authority websites in the target niche, qualify them for link-building opportunities, and send personalized outreach proposing mutually beneficial content collaborations",
        setup:
          "Set OpenAI API key for site qualification, content analysis, and outreach drafting. Configure site-owner discovery API (Hunter.io). Set email validation API credentials. Set Google Sheets credentials for link tracker.",
        inputFields: [
          {
            name: "target_niche",
            type: "string",
            example: "B2B SaaS marketing technology",
          },
          {
            name: "content_url",
            type: "string",
            example: "https://blog.company.com/ultimate-guide-sales-automation",
          },
          {
            name: "content_title",
            type: "string",
            example: "The Ultimate Guide to Sales Automation in 2026",
          },
          { name: "min_domain_authority", type: "string", example: "40" },
        ],
      }),

      // 2. AI target-site qualification
      aiNode(
        "2",
        "AI Qualify Target Sites",
        280,
        "You are an SEO link-building strategist. Given a target niche and minimum domain authority threshold, identify high-quality websites that are likely to link to content in this space. Consider domain authority, content relevance, link-out patterns, and editorial quality. Respond JSON: {target_site: string, target_domain: string, estimated_da: number, content_relevance: string, link_out_pattern: string, editorial_quality: string, link_type_opportunity: string, contact_page_exists: string}",
        "Niche: {target_niche}. Min DA: {min_domain_authority}. Our content: {content_title} ({content_url}). Identify a high-quality link prospect site.",
        [
          { field: "target_site", as: "target_site", type: "string" },
          { field: "target_domain", as: "target_domain", type: "string" },
          { field: "estimated_da", as: "estimated_da", type: "number" },
          {
            field: "content_relevance",
            as: "content_relevance",
            type: "string",
          },
          { field: "link_out_pattern", as: "link_out_pattern", type: "string" },
          {
            field: "editorial_quality",
            as: "editorial_quality",
            type: "string",
          },
          {
            field: "link_type_opportunity",
            as: "link_opportunity",
            type: "string",
          },
          {
            field: "contact_page_exists",
            as: "contact_page_exists",
            type: "string",
          },
        ],
      ),

      // 3. AI content-match analysis
      aiNode(
        "3",
        "AI Analyze Content Match",
        520,
        "You are a content strategist. Analyze how well the source content matches the target site's audience and content patterns. Identify the specific page or section on the target site where a link would be most natural. Suggest the anchor text and context. Respond JSON: {match_score: number, best_placement_page: string, suggested_anchor_text: string, link_context: string, mutual_benefit: string, content_gap_filled: string}",
        "Our content: {content_title} ({content_url}). Target site: {target_site} ({target_domain}). Relevance: {content_relevance}. Link pattern: {link_out_pattern}. Quality: {editorial_quality}. Opportunity: {link_opportunity}. Analyze the content match.",
        [
          { field: "match_score", as: "match_score", type: "number" },
          {
            field: "best_placement_page",
            as: "best_placement_page",
            type: "string",
          },
          { field: "suggested_anchor_text", as: "anchor_text", type: "string" },
          { field: "link_context", as: "link_context", type: "string" },
          { field: "mutual_benefit", as: "mutual_benefit", type: "string" },
          { field: "content_gap_filled", as: "content_gap", type: "string" },
        ],
      ),

      // 4. AI value-proposition development
      aiNode(
        "4",
        "AI Develop Link Value Proposition",
        760,
        "You are a relationship-focused SEO outreach specialist. Develop a compelling value proposition for why the target site should link to the content. Focus on how it helps their readers, fills a content gap, and adds authority. Avoid sounding transactional. Respond JSON: {value_prop_summary: string, reader_benefit: string, authority_boost: string, reciprocal_offer: string}",
        "Our content: {content_title}. Target: {target_site}. Match score: {match_score}/100. Placement: {best_placement_page}. Mutual benefit: {mutual_benefit}. Gap filled: {content_gap}. Context: {link_context}. Develop the value proposition.",
        [
          { field: "value_prop_summary", as: "value_prop", type: "string" },
          { field: "reader_benefit", as: "reader_benefit", type: "string" },
          { field: "authority_boost", as: "authority_boost", type: "string" },
          { field: "reciprocal_offer", as: "reciprocal_offer", type: "string" },
        ],
      ),

      // 5. Site-owner discovery API
      n("5", "hitApi", "Discover Site Owner via Hunter.io", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={target_domain}&api_key={hunter_api_key}&limit=5&department=marketing,executive",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "owner_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "owner_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "owner_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "owner_position",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Site Owner Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={owner_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI outreach draft + link tracker Sheets
      sheetsNode(
        "7",
        "Log Link Prospect to Tracker",
        1480,
        "LinkProspectTracker",
        '[["{target_site}","{target_domain}","{estimated_da}","{content_title}","{match_score}","{anchor_text}","{owner_first_name} {owner_last_name}","{owner_email}","{email_status}","{link_opportunity}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1820. LINKEDIN KEYWORD SEARCH BUILD (8 nodes)
  // ===================================================================

  "LinkedIn Keyword Search Build": {
    summary:
      "Fires on webhook with LinkedIn search results, enriches profiles via Clearbit API, analyzes personas with AI, scores ICP fit with runCode, discovers emails via API, validates via API waterfall, pushes contacts to HubSpot with persona tags, and assigns SDR queues via Slack.",
    nodes: [
      trigger("Webhook: LinkedIn search results received", "webhook_app", {
        source: "linkedin_tools",
        event: "search.results_ready",
        howItStarts:
          "Fires when a batch of LinkedIn search results is received from a scraping tool or Sales Navigator export — enriching, scoring, and routing each profile through the prospecting pipeline",
        setup:
          "Configure webhook to receive LinkedIn search results from your scraping/export tool. Set Clearbit API key for enrichment. Set OpenAI API key for persona analysis. Configure email discovery API (Hunter.io or Apollo). Set email validation API. Provide HubSpot token and Slack webhook.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook
      aiNode(
        "2",
        "AI Extract LinkedIn Profile Data",
        280,
        "You are a data extraction specialist. Parse the raw webhook payload containing LinkedIn search results. Extract the first profile's name, headline, company, location, LinkedIn URL, and any other available fields like connections count or activity level. Respond JSON: {profile_name: string, profile_headline: string, profile_company: string, profile_location: string, linkedin_url: string, profile_industry: string, connection_count: string, activity_level: string}",
        "Raw LinkedIn search results webhook payload: {webhook_payload}. Extract the first profile's details regardless of field naming.",
        [
          { field: "profile_name", as: "profile_name", type: "string" },
          { field: "profile_headline", as: "profile_headline", type: "string" },
          { field: "profile_company", as: "profile_company", type: "string" },
          { field: "profile_location", as: "profile_location", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "profile_industry", as: "profile_industry", type: "string" },
          { field: "connection_count", as: "connection_count", type: "string" },
          { field: "activity_level", as: "activity_level", type: "string" },
        ],
      ),

      // 3. Profile enrichment Clearbit API
      n("3", "hitApi", "Enrich Profile via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={profile_company}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
            { name: "domain", customName: "company_domain", type: "string" },
          ],
        },
      }),

      // 4. AI persona analysis
      aiNode(
        "4",
        "AI Analyze Prospect Persona",
        760,
        "You are a sales intelligence analyst. Given the LinkedIn profile headline, company details, and enrichment data, analyze the prospect's persona. Determine their likely role in purchasing decisions, pain points based on their title and industry, engagement preferences, and the best outreach approach. Respond JSON: {persona_type: string, seniority_level: string, buying_role: string, likely_pain_points: string, engagement_preference: string, best_outreach_channel: string, personalization_hooks: string}",
        "Name: {profile_name}. Headline: {profile_headline}. Company: {company_name} ({company_industry}). Size: {company_size}. Revenue: {company_revenue}. Location: {profile_location}. Tech: {tech_stack}. Analyze their persona for outreach.",
        [
          { field: "persona_type", as: "persona_type", type: "string" },
          { field: "seniority_level", as: "seniority_level", type: "string" },
          { field: "buying_role", as: "buying_role", type: "string" },
          { field: "likely_pain_points", as: "pain_points", type: "string" },
          {
            field: "engagement_preference",
            as: "engagement_pref",
            type: "string",
          },
          {
            field: "best_outreach_channel",
            as: "best_channel",
            type: "string",
          },
          {
            field: "personalization_hooks",
            as: "personalization_hooks",
            type: "string",
          },
        ],
      ),

      // 5. ICP scoring runCode
      n("5", "runCode", "Score ICP Fit", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score ICP fit based on persona, company, and enrichment data
const seniority = (vars.seniority_level || "").toLowerCase();
const buyingRole = (vars.buying_role || "").toLowerCase();
const companySize = parseInt(vars.company_size) || 0;
const industry = (vars.company_industry || "").toLowerCase();
const revenue = (vars.company_revenue || "").toLowerCase();
let icp_score = 30;
// Seniority scoring (0-25)
if (seniority.includes("c-level") || seniority.includes("executive") || seniority.includes("vp")) icp_score += 25;
else if (seniority.includes("director") || seniority.includes("head")) icp_score += 20;
else if (seniority.includes("manager") || seniority.includes("senior")) icp_score += 12;
else icp_score += 5;
// Buying role (0-20)
if (buyingRole.includes("decision_maker") || buyingRole.includes("buyer")) icp_score += 20;
else if (buyingRole.includes("champion") || buyingRole.includes("influencer")) icp_score += 15;
else if (buyingRole.includes("evaluator")) icp_score += 10;
// Company size (0-15)
if (companySize >= 100 && companySize <= 5000) icp_score += 15;
else if (companySize >= 50 || companySize > 5000) icp_score += 8;
// Industry fit (0-10)
const targetIndustries = ["technology", "saas", "software", "fintech", "marketing"];
if (targetIndustries.some(t => industry.includes(t))) icp_score += 10;
else icp_score += 3;
const final_score = Math.min(100, icp_score);
const tier = final_score >= 80 ? "tier_1" : final_score >= 60 ? "tier_2" : "tier_3";
return { icp_score: final_score, prospect_tier: tier, is_qualified: final_score >= 55 ? "true" : "false" };`,
          outputVariables: [
            { name: "icp_score", type: "number" },
            { name: "prospect_tier", type: "string" },
            { name: "is_qualified", type: "string" },
          ],
        },
      }),

      // 6. Email discovery API
      n("6", "hitApi", "Discover Email via Hunter.io", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={company_domain}&first_name={profile_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.email",
              customName: "prospect_email",
              type: "string",
            },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 7. Validation waterfall API + CRM push HubSpot
      hubspotPostNode(
        "7",
        "Push Contact to HubSpot with Tags",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{prospect_email}","firstname":"{profile_name}","company":"{company_name}","jobtitle":"{profile_headline}","industry":"{company_industry}","hs_lead_status":"NEW","lead_source":"linkedin_search","persona_type":"{persona_type}","seniority_level":"{seniority_level}","icp_score":"{icp_score}","prospect_tier":"{prospect_tier}","buying_role":"{buying_role}","linkedin_url":"{linkedin_url}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
        ],
      ),

      // 8. SDR queue assignment Slack
      slackNode(
        "8",
        "Assign to SDR Queue via Slack",
        1720,
        ":linkedin: *New LinkedIn Prospect Qualified*\n*Name:* {profile_name}\n*Title:* {profile_headline}\n*Company:* {company_name} ({company_industry})\n*Size:* {company_size} | *Revenue:* {company_revenue}\n*ICP Score:* {icp_score}/100 | *Tier:* {prospect_tier}\n*Persona:* {persona_type} | *Buying Role:* {buying_role}\n*Best Channel:* {best_channel}\n*Pain Points:* {pain_points}\n*Hooks:* {personalization_hooks}\n*Email:* {prospect_email} (confidence: {email_confidence}%)\n*LinkedIn:* {linkedin_url}\n*HubSpot ID:* {contact_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1821. LINKEDIN POST ENGAGER CAPTURE (8 nodes)
  // ===================================================================

  "LinkedIn Post Engager Capture": {
    summary:
      "Fires on webhook with LinkedIn engagement data, enriches engagers via Clearbit API, analyzes engagement quality with AI, scores ICP engagement with runCode, gates on threshold via condition, resolves emails via API, validates via API, and creates outreach entries with CRM logging in HubSpot.",
    nodes: [
      trigger("Webhook: LinkedIn engagement captured", "webhook_app", {
        source: "linkedin_automation",
        event: "post.engagement_captured",
        howItStarts:
          "Fires when someone likes, comments on, or shares a company LinkedIn post — capturing the engager's profile data to qualify and convert engaged prospects into outreach targets",
        setup:
          "Configure LinkedIn engagement capture webhook (via Phantombuster, Dux-Soup, or similar). Set Clearbit API key for enrichment. Set OpenAI API key for engagement analysis. Configure email discovery API. Set email validation API. Provide HubSpot token.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook
      aiNode(
        "2",
        "AI Extract Engagement Data",
        280,
        "You are a social media data parser. Extract engagement details from the raw webhook payload. Find the engager's name, profile headline, company, engagement type (like, comment, share), comment text if applicable, LinkedIn URL, post URL, and timestamp. Respond JSON: {engager_name: string, engager_headline: string, engager_company: string, engagement_type: string, comment_text: string, linkedin_url: string, post_url: string, engagement_timestamp: string}",
        "Raw LinkedIn engagement webhook payload: {webhook_payload}. Extract all engagement details regardless of field naming conventions.",
        [
          { field: "engager_name", as: "engager_name", type: "string" },
          { field: "engager_headline", as: "engager_headline", type: "string" },
          { field: "engager_company", as: "engager_company", type: "string" },
          { field: "engagement_type", as: "engagement_type", type: "string" },
          { field: "comment_text", as: "comment_text", type: "string" },
          { field: "linkedin_url", as: "engager_linkedin", type: "string" },
          { field: "post_url", as: "post_url", type: "string" },
          {
            field: "engagement_timestamp",
            as: "engagement_time",
            type: "string",
          },
        ],
      ),

      // 3. Engager enrichment Clearbit API
      n("3", "hitApi", "Enrich Engager Company via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={engager_company}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            { name: "domain", customName: "company_domain", type: "string" },
          ],
        },
      }),

      // 4. AI engagement-quality analysis
      aiNode(
        "4",
        "AI Analyze Engagement Quality",
        760,
        "You are a social selling analyst. Analyze the quality and intent behind this LinkedIn engagement. Consider the engagement type (comment > share > like), the comment content for buying signals, the engager's seniority based on headline, and whether their company fits the target profile. Respond JSON: {engagement_quality_score: number, intent_signal: string, buying_signal_detected: string, seniority_assessment: string, fit_assessment: string, recommended_action: string, urgency: string}",
        "Engager: {engager_name}. Headline: {engager_headline}. Company: {company_name} ({company_industry}). Size: {company_size}. Revenue: {company_revenue}. Engagement: {engagement_type}. Comment: {comment_text}. Post: {post_url}. Analyze engagement quality.",
        [
          {
            field: "engagement_quality_score",
            as: "engagement_quality",
            type: "number",
          },
          { field: "intent_signal", as: "intent_signal", type: "string" },
          {
            field: "buying_signal_detected",
            as: "buying_signal",
            type: "string",
          },
          { field: "seniority_assessment", as: "seniority", type: "string" },
          { field: "fit_assessment", as: "fit_assessment", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "urgency", as: "outreach_urgency", type: "string" },
        ],
      ),

      // 5. ICP engagement scoring runCode
      n("5", "runCode", "Score ICP Engagement Fit", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score combined ICP and engagement fit
const engagementQuality = parseInt(vars.engagement_quality) || 0;
const buying = (vars.buying_signal || "").toLowerCase();
const seniority = (vars.seniority || "").toLowerCase();
const fit = (vars.fit_assessment || "").toLowerCase();
const engType = (vars.engagement_type || "").toLowerCase();
let score = 0;
// Engagement quality base (0-30)
score += Math.round(engagementQuality * 0.3);
// Engagement type bonus
if (engType.includes("comment")) score += 20;
else if (engType.includes("share")) score += 15;
else if (engType.includes("like")) score += 5;
// Buying signal (0-20)
if (buying.includes("strong") || buying === "yes") score += 20;
else if (buying.includes("moderate") || buying.includes("possible")) score += 10;
// Seniority (0-15)
if (seniority.includes("c-level") || seniority.includes("executive") || seniority.includes("vp")) score += 15;
else if (seniority.includes("director") || seniority.includes("senior")) score += 10;
else score += 3;
// Fit (0-15)
if (fit.includes("strong") || fit.includes("excellent")) score += 15;
else if (fit.includes("good") || fit.includes("moderate")) score += 8;
const final_score = Math.min(100, score);
return { icp_engagement_score: final_score, meets_threshold: final_score >= 50 ? "true" : "false", priority_level: final_score >= 75 ? "high" : final_score >= 50 ? "medium" : "low" };`,
          outputVariables: [
            { name: "icp_engagement_score", type: "number" },
            { name: "meets_threshold", type: "string" },
            { name: "priority_level", type: "string" },
          ],
        },
      }),

      // 6. Threshold gate condition
      n("6", "condition", "Does Engager Meet Outreach Threshold?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "meets_threshold", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Email resolution API
      n("7", "hitApi", "Discover Engager Email via Hunter.io", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={company_domain}&first_name={engager_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "data.email", customName: "engager_email", type: "string" },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 8. Outreach + CRM entry HubSpot
      hubspotPostNode(
        "8",
        "Create Engager Contact in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{engager_email}","firstname":"{engager_name}","company":"{company_name}","jobtitle":"{engager_headline}","industry":"{company_industry}","hs_lead_status":"NEW","lead_source":"linkedin_engagement","engagement_type":"{engagement_type}","engagement_score":"{icp_engagement_score}","intent_signal":"{intent_signal}","buying_signal":"{buying_signal}","priority_level":"{priority_level}","recommended_action":"{recommended_action}","linkedin_url":"{engager_linkedin}","source_post":"{post_url}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1822. LOCAL BUSINESS PARTNERSHIP (7 nodes)
  // ===================================================================

  "Local Business Partnership": {
    summary:
      "Runs on schedule to discover local businesses with AI, research their profiles with AI, identify partnership opportunities with AI, discover owner/manager contacts via API, validate emails, draft community partnership proposals with AI, and log to a tracker in Sheets.",
    nodes: [
      trigger("Schedule: Bi-weekly local business outreach", "schedule", {
        source: "business_development",
        event: "local.partnership_outreach",
        schedule: "Every other Wednesday at 10:00 AM",
        howItStarts:
          "Runs bi-weekly to discover local businesses in the target area that complement the service offering, research their profiles, and send personalized community partnership proposals",
        setup:
          "Set OpenAI API key for business discovery, research, and proposal generation. Configure contact discovery API. Set email validation API credentials. Set Google Sheets credentials for partnership tracker.",
        inputFields: [
          {
            name: "target_area",
            type: "string",
            example: "Austin, TX metropolitan area",
          },
          {
            name: "business_category",
            type: "string",
            example:
              "coworking spaces, startup accelerators, tech meetup venues",
          },
          {
            name: "partnership_type",
            type: "string",
            example: "cross-referral, co-hosted events, bundled services",
          },
          { name: "company_name", type: "string", example: "OmniEngage" },
        ],
      }),

      // 2. AI local-business discovery
      aiNode(
        "2",
        "AI Discover Local Businesses",
        280,
        "You are a local business development researcher. Given a target area and business category, identify a specific local business that would be an ideal partnership candidate. Research their reputation, customer base, online presence, and community involvement. Respond JSON: {business_name: string, business_domain: string, business_type: string, location: string, reputation_score: string, customer_base: string, online_presence: string, community_involvement: string}",
        "Area: {target_area}. Category: {business_category}. Partnership type: {partnership_type}. Our company: {company_name}. Discover the best local business partner candidate.",
        [
          { field: "business_name", as: "local_business", type: "string" },
          { field: "business_domain", as: "business_domain", type: "string" },
          { field: "business_type", as: "business_type", type: "string" },
          { field: "location", as: "business_location", type: "string" },
          { field: "reputation_score", as: "reputation_score", type: "string" },
          { field: "customer_base", as: "customer_base", type: "string" },
          { field: "online_presence", as: "online_presence", type: "string" },
          {
            field: "community_involvement",
            as: "community_involvement",
            type: "string",
          },
        ],
      ),

      // 3. AI business-profile research
      aiNode(
        "3",
        "AI Research Business Profile",
        520,
        "You are a partnership analyst. Deep-dive into the local business profile. Analyze their service offerings, pricing model, target demographics, competitive positioning, and growth trajectory. Identify synergies with the proposing company. Respond JSON: {services_offered: string, pricing_model: string, target_demographics: string, competitive_position: string, growth_trajectory: string, synergy_areas: string, partnership_value: string}",
        "Business: {local_business} ({business_type}). Location: {business_location}. Customer base: {customer_base}. Reputation: {reputation_score}. Online: {online_presence}. Community: {community_involvement}. Our company: {company_name}. Research their profile for partnership synergies.",
        [
          { field: "services_offered", as: "services_offered", type: "string" },
          { field: "pricing_model", as: "pricing_model", type: "string" },
          {
            field: "target_demographics",
            as: "target_demographics",
            type: "string",
          },
          {
            field: "competitive_position",
            as: "competitive_position",
            type: "string",
          },
          {
            field: "growth_trajectory",
            as: "growth_trajectory",
            type: "string",
          },
          { field: "synergy_areas", as: "synergy_areas", type: "string" },
          {
            field: "partnership_value",
            as: "partnership_value",
            type: "string",
          },
        ],
      ),

      // 4. AI partnership-opportunity identification
      aiNode(
        "4",
        "AI Identify Partnership Opportunities",
        760,
        "You are a community partnerships manager. Given the business profile, synergies, and partnership type preferences, identify 2-3 specific partnership opportunities. For each, describe the format, mutual benefits, estimated reach, and implementation timeline. Respond JSON: {opportunity_1: string, opportunity_2: string, mutual_benefits: string, estimated_reach: string, implementation_timeline: string, pitch_angle: string}",
        "Business: {local_business} ({business_type}) in {business_location}. Services: {services_offered}. Demographics: {target_demographics}. Synergies: {synergy_areas}. Value: {partnership_value}. Preferred types: {partnership_type}. Company: {company_name}. Identify specific opportunities.",
        [
          { field: "opportunity_1", as: "opportunity_1", type: "string" },
          { field: "opportunity_2", as: "opportunity_2", type: "string" },
          { field: "mutual_benefits", as: "mutual_benefits", type: "string" },
          { field: "estimated_reach", as: "estimated_reach", type: "string" },
          {
            field: "implementation_timeline",
            as: "impl_timeline",
            type: "string",
          },
          { field: "pitch_angle", as: "pitch_angle", type: "string" },
        ],
      ),

      // 5. Owner manager discovery API
      n("5", "hitApi", "Discover Owner/Manager Contact", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={business_domain}&api_key={hunter_api_key}&limit=3&seniority=executive,senior",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "owner_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "owner_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "owner_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "owner_title",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Owner Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={owner_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI community partnership proposal + tracker Sheets
      sheetsNode(
        "7",
        "Log Partnership to Tracker",
        1480,
        "LocalPartnerTracker",
        '[["{local_business}","{business_type}","{business_location}","{owner_first_name} {owner_last_name}","{owner_email}","{email_status}","{opportunity_1}","{opportunity_2}","{mutual_benefits}","{estimated_reach}","{impl_timeline}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1823. LOCAL BUSINESSES FOR PAID-ADS OUTREACH (8 nodes)
  // ===================================================================

  "Local Businesses for Paid-Ads Outreach": {
    summary:
      "Runs on schedule to discover local businesses via API, analyze their current ads with runCode, estimate ad potential with runCode, classify opportunities via condition, discover owner contacts via API, generate tailored paid-ads pitches with AI, and send with pipeline entry in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly local ads prospect scan", "schedule", {
        source: "agency_sales",
        event: "local.ads_prospect_scan",
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to discover local businesses that could benefit from paid advertising services — analyzing their current ad presence, estimating potential, and sending personalized service pitches",
        setup:
          "Set local business discovery API credentials (Google Places or Yelp). Set OpenAI API key for pitch generation. Configure owner contact discovery API. Provide HubSpot token for pipeline management.",
        inputFields: [
          {
            name: "target_location",
            type: "string",
            example: "San Francisco Bay Area",
          },
          {
            name: "business_vertical",
            type: "string",
            example: "restaurants, home services, fitness studios",
          },
          {
            name: "service_offering",
            type: "string",
            example: "Google Ads + Meta Ads management",
          },
        ],
      }),

      // 2. Local-business discovery API
      n("2", "hitApi", "Discover Local Businesses via Google Places", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://maps.googleapis.com/maps/api/place/textsearch/json?query={business_vertical}+in+{target_location}&key={google_places_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "results[0].name",
              customName: "business_name",
              type: "string",
            },
            {
              name: "results[0].formatted_address",
              customName: "business_address",
              type: "string",
            },
            {
              name: "results[0].rating",
              customName: "business_rating",
              type: "number",
            },
            {
              name: "results[0].user_ratings_total",
              customName: "total_reviews",
              type: "number",
            },
            {
              name: "results[0].types[0]",
              customName: "business_category",
              type: "string",
            },
            {
              name: "results[0].business_status",
              customName: "business_status",
              type: "string",
            },
            {
              name: "results.length",
              customName: "total_businesses_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. Current-ad analysis runCode
      n("3", "runCode", "Analyze Current Ad Presence", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze the business's current ad presence indicators
const name = vars.business_name || "";
const rating = parseFloat(vars.business_rating) || 0;
const reviews = parseInt(vars.total_reviews) || 0;
const category = (vars.business_category || "").toLowerCase();
const status = (vars.business_status || "").toLowerCase();
// Estimate ad sophistication from public signals
let ad_maturity = "none";
let estimated_monthly_spend = 0;
let ad_gap_analysis = "";
if (reviews >= 200 && rating >= 4.0) {
  ad_maturity = "some_presence";
  estimated_monthly_spend = 500;
  ad_gap_analysis = "Business has traction but likely relies on organic reviews. Paid ads could accelerate growth significantly.";
} else if (reviews >= 50) {
  ad_maturity = "minimal";
  estimated_monthly_spend = 100;
  ad_gap_analysis = "Moderate review base suggests some marketing effort but no structured ad strategy. High potential for paid acquisition.";
} else {
  ad_maturity = "none";
  estimated_monthly_spend = 0;
  ad_gap_analysis = "Low online presence indicates no advertising investment. Green field opportunity for building entire paid channel.";
}
const opportunity_size = ad_maturity === "none" ? "large" : ad_maturity === "minimal" ? "medium" : "expansion";
return { ad_maturity: ad_maturity, estimated_current_spend: estimated_monthly_spend, ad_gap_analysis: ad_gap_analysis, opportunity_size: opportunity_size, is_operational: status.includes("operational") ? "true" : "true" };`,
          outputVariables: [
            { name: "ad_maturity", type: "string" },
            { name: "estimated_current_spend", type: "number" },
            { name: "ad_gap_analysis", type: "string" },
            { name: "opportunity_size", type: "string" },
            { name: "is_operational", type: "string" },
          ],
        },
      }),

      // 4. Ad-potential estimation runCode
      n("4", "runCode", "Estimate Ad Potential", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Estimate the advertising potential and recommended budget
const oppSize = (vars.opportunity_size || "").toLowerCase();
const rating = parseFloat(vars.business_rating) || 0;
const reviews = parseInt(vars.total_reviews) || 0;
const category = (vars.business_category || "").toLowerCase();
let recommended_budget = 0;
let estimated_roi = "";
let campaign_types = "";
let priority = "low";
if (oppSize === "large") {
  recommended_budget = 2000;
  estimated_roi = "3-5x within 90 days";
  campaign_types = "Google Search, Google Maps, Meta Local Awareness, Retargeting";
  priority = "high";
} else if (oppSize === "medium") {
  recommended_budget = 1500;
  estimated_roi = "2-4x within 90 days";
  campaign_types = "Google Search, Google Maps, Meta Lead Gen";
  priority = "medium";
} else {
  recommended_budget = 3000;
  estimated_roi = "1.5-3x with optimization";
  campaign_types = "Scaling existing campaigns, A/B testing, audience expansion";
  priority = "medium";
}
// Adjust for rating quality
if (rating >= 4.5) recommended_budget = Math.round(recommended_budget * 1.2);
const is_qualified = priority !== "low" ? "true" : "false";
return { recommended_budget: recommended_budget, estimated_roi: estimated_roi, campaign_types: campaign_types, prospect_priority: priority, is_qualified_prospect: is_qualified };`,
          outputVariables: [
            { name: "recommended_budget", type: "number" },
            { name: "estimated_roi", type: "string" },
            { name: "campaign_types", type: "string" },
            { name: "prospect_priority", type: "string" },
            { name: "is_qualified_prospect", type: "string" },
          ],
        },
      }),

      // 5. Opportunity classification condition
      n("5", "condition", "Is Qualified Ads Prospect?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "is_qualified_prospect",
              operator: "eq",
              value: "true",
            },
          ],
        },
      }),

      // 6. Owner contact discovery API
      n("6", "hitApi", "Discover Business Owner Contact", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={business_name}&api_key={hunter_api_key}&limit=3&seniority=executive",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "owner_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "owner_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "owner_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 7. AI tailored paid-ads pitch
      aiNode(
        "7",
        "AI Draft Paid-Ads Pitch",
        1480,
        "You are a local advertising consultant. Write a personalized pitch email to a local business owner explaining how paid ads could grow their business. Reference their specific business, current online presence, rating, and the opportunity gap. Include recommended budget, expected ROI, and campaign types. Keep under 200 words, friendly and educational. Respond JSON: {pitch_subject: string, pitch_body: string}",
        "Business: {business_name} ({business_category}) in {business_address}. Rating: {business_rating}/5 ({total_reviews} reviews). Ad maturity: {ad_maturity}. Gap: {ad_gap_analysis}. Opportunity: {opportunity_size}. Recommended budget: ${recommended_budget}/mo. ROI: {estimated_roi}. Campaigns: {campaign_types}. Service: {service_offering}. Write the pitch.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
        ],
      ),

      // 8. Send + pipeline entry HubSpot
      hubspotPostNode(
        "8",
        "Create Ads Prospect in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{owner_email}","firstname":"{owner_first_name}","lastname":"{owner_last_name}","company":"{business_name}","hs_lead_status":"NEW","lead_source":"local_ads_outreach","business_category":"{business_category}","business_rating":"{business_rating}","ad_maturity":"{ad_maturity}","opportunity_size":"{opportunity_size}","recommended_budget":"{recommended_budget}","estimated_roi":"{estimated_roi}","prospect_priority":"{prospect_priority}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1824. LOCAL BUSINESSES FOR SEO OUTREACH (8 nodes)
  // ===================================================================

  "Local Businesses for SEO Outreach": {
    summary:
      "Runs on schedule to discover local businesses via API, perform SEO gap analysis with runCode, benchmark competitors with runCode, score SEO opportunities with runCode, discover owner contacts via API, generate tailored SEO audit pitches with AI, and send with CRM entry in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly local SEO prospect scan", "schedule", {
        source: "agency_sales",
        event: "local.seo_prospect_scan",
        schedule: "Every Tuesday at 9:00 AM",
        howItStarts:
          "Runs weekly to discover local businesses with poor SEO performance that could benefit from optimization services — analyzing their search visibility gaps and sending personalized audit pitches",
        setup:
          "Set local business discovery API credentials (Google Places). Set OpenAI API key for SEO pitch generation. Configure owner contact discovery API. Provide HubSpot token for CRM entry.",
        inputFields: [
          { name: "target_location", type: "string", example: "Denver, CO" },
          {
            name: "business_vertical",
            type: "string",
            example: "dentists, plumbers, law firms",
          },
          {
            name: "seo_service_offering",
            type: "string",
            example:
              "Local SEO, Google Business Profile optimization, content strategy",
          },
        ],
      }),

      // 2. Local-business discovery API
      n("2", "hitApi", "Discover Local Businesses via Google Places", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://maps.googleapis.com/maps/api/place/textsearch/json?query={business_vertical}+in+{target_location}&key={google_places_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "results[0].name",
              customName: "business_name",
              type: "string",
            },
            {
              name: "results[0].formatted_address",
              customName: "business_address",
              type: "string",
            },
            {
              name: "results[0].rating",
              customName: "business_rating",
              type: "number",
            },
            {
              name: "results[0].user_ratings_total",
              customName: "total_reviews",
              type: "number",
            },
            {
              name: "results[0].types[0]",
              customName: "business_category",
              type: "string",
            },
            {
              name: "results.length",
              customName: "total_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. SEO gap analysis runCode
      n("3", "runCode", "Perform SEO Gap Analysis", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze SEO gaps based on available signals
const name = vars.business_name || "";
const rating = parseFloat(vars.business_rating) || 0;
const reviews = parseInt(vars.total_reviews) || 0;
const category = (vars.business_category || "").toLowerCase();
const address = (vars.business_address || "").toLowerCase();
let seo_health = "poor";
let gaps = [];
// Review-based SEO signals
if (reviews < 20) {
  gaps.push("Very low review count — critical for local SEO ranking");
  seo_health = "poor";
} else if (reviews < 50) {
  gaps.push("Below-average review count for competitive local market");
  seo_health = "below_average";
} else if (reviews < 100) {
  seo_health = "average";
  gaps.push("Moderate reviews but room for significant growth");
} else {
  seo_health = "good";
  gaps.push("Strong review base — optimize for featured snippets and map pack");
}
// Common local SEO gaps
gaps.push("Likely missing schema markup for local business");
gaps.push("Google Business Profile probably not fully optimized");
if (reviews > 0 && rating < 4.0) gaps.push("Below 4-star rating hurting click-through rates");
const gap_summary = gaps.join("; ");
const estimated_traffic_uplift = seo_health === "poor" ? "200-400%" : seo_health === "below_average" ? "100-200%" : seo_health === "average" ? "50-100%" : "20-50%";
return { seo_health: seo_health, gap_summary: gap_summary, gap_count: gaps.length, estimated_traffic_uplift: estimated_traffic_uplift };`,
          outputVariables: [
            { name: "seo_health", type: "string" },
            { name: "gap_summary", type: "string" },
            { name: "gap_count", type: "number" },
            { name: "estimated_traffic_uplift", type: "string" },
          ],
        },
      }),

      // 4. Competitor benchmarking runCode
      n("4", "runCode", "Benchmark Against Competitors", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Benchmark the business against local competitors
const totalFound = parseInt(vars.total_found) || 1;
const reviews = parseInt(vars.total_reviews) || 0;
const rating = parseFloat(vars.business_rating) || 0;
const seoHealth = vars.seo_health || "poor";
// Estimate competitive position
let competitive_rank = "bottom_third";
if (reviews >= 100 && rating >= 4.5) competitive_rank = "top_third";
else if (reviews >= 30 && rating >= 4.0) competitive_rank = "middle_third";
const market_density = totalFound >= 15 ? "high" : totalFound >= 5 ? "medium" : "low";
const difficulty = market_density === "high" ? "competitive" : market_density === "medium" ? "moderate" : "easy_win";
const estimated_competitors = Math.min(20, totalFound);
const benchmark_insight = competitive_rank === "bottom_third"
  ? "Currently behind most competitors in online visibility. Significant opportunity to leapfrog with proper SEO."
  : competitive_rank === "middle_third"
  ? "Middle of the pack — targeted optimizations could push into top 3 positions."
  : "Already strong — advanced strategies needed for dominance.";
return { competitive_rank: competitive_rank, market_density: market_density, difficulty_level: difficulty, estimated_competitors: estimated_competitors, benchmark_insight: benchmark_insight };`,
          outputVariables: [
            { name: "competitive_rank", type: "string" },
            { name: "market_density", type: "string" },
            { name: "difficulty_level", type: "string" },
            { name: "estimated_competitors", type: "number" },
            { name: "benchmark_insight", type: "string" },
          ],
        },
      }),

      // 5. SEO opportunity scoring runCode
      n("5", "runCode", "Score SEO Opportunity", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the overall SEO opportunity for this prospect
const health = (vars.seo_health || "").toLowerCase();
const gapCount = parseInt(vars.gap_count) || 0;
const rank = (vars.competitive_rank || "").toLowerCase();
const difficulty = (vars.difficulty_level || "").toLowerCase();
const uplift = vars.estimated_traffic_uplift || "";
let score = 30;
// Health gaps = opportunity (0-30)
if (health === "poor") score += 30;
else if (health === "below_average") score += 22;
else if (health === "average") score += 12;
else score += 5;
// Competitive position (0-20)
if (rank.includes("bottom")) score += 20;
else if (rank.includes("middle")) score += 12;
else score += 5;
// Market difficulty (0-20)
if (difficulty === "easy_win") score += 20;
else if (difficulty === "moderate") score += 12;
else score += 5;
const final_score = Math.min(100, score);
const recommended_package = final_score >= 75 ? "comprehensive" : final_score >= 50 ? "standard" : "starter";
const estimated_monthly_fee = recommended_package === "comprehensive" ? 2500 : recommended_package === "standard" ? 1500 : 750;
return { seo_opportunity_score: final_score, recommended_package: recommended_package, estimated_monthly_fee: estimated_monthly_fee, is_qualified: final_score >= 45 ? "true" : "false" };`,
          outputVariables: [
            { name: "seo_opportunity_score", type: "number" },
            { name: "recommended_package", type: "string" },
            { name: "estimated_monthly_fee", type: "number" },
            { name: "is_qualified", type: "string" },
          ],
        },
      }),

      // 6. Owner contact discovery API
      n("6", "hitApi", "Discover Business Owner Contact", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={business_name}&api_key={hunter_api_key}&limit=3&seniority=executive",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "owner_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "owner_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "owner_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 7. AI SEO audit pitch
      aiNode(
        "7",
        "AI Draft SEO Audit Pitch",
        1480,
        "You are a local SEO consultant. Write a personalized pitch email to a local business owner offering a free SEO audit. Reference their specific business, current online presence signals, competitive positioning, and the estimated improvement opportunity. Be educational, not pushy. Include specific numbers. Under 200 words. Respond JSON: {pitch_subject: string, pitch_body: string}",
        "Business: {business_name} ({business_category}) at {business_address}. Rating: {business_rating}/5 ({total_reviews} reviews). SEO health: {seo_health}. Gaps: {gap_summary}. Traffic uplift potential: {estimated_traffic_uplift}. Competitive rank: {competitive_rank}. Market density: {market_density}. Benchmark: {benchmark_insight}. Opportunity score: {seo_opportunity_score}/100. Package: {recommended_package} (${estimated_monthly_fee}/mo). Service: {seo_service_offering}. Write the pitch.",
        [
          { field: "pitch_subject", as: "seo_pitch_subject", type: "string" },
          { field: "pitch_body", as: "seo_pitch_body", type: "string" },
        ],
      ),

      // 8. Send + CRM entry HubSpot
      hubspotPostNode(
        "8",
        "Create SEO Prospect in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{owner_email}","firstname":"{owner_first_name}","lastname":"{owner_last_name}","company":"{business_name}","hs_lead_status":"NEW","lead_source":"local_seo_outreach","business_category":"{business_category}","seo_health":"{seo_health}","opportunity_score":"{seo_opportunity_score}","recommended_package":"{recommended_package}","estimated_fee":"{estimated_monthly_fee}","traffic_uplift":"{estimated_traffic_uplift}","competitive_rank":"{competitive_rank}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1825. LOST BACKLINK RECLAIM (7 nodes)
  // ===================================================================

  "Lost Backlink Reclaim": {
    summary:
      "Runs on schedule to detect lost backlinks via API, categorize link losses with runCode, select recovery strategies via condition, discover site contacts via API, draft reclaim emails with AI, send outreach, and log to a recovery tracker in Sheets.",
    nodes: [
      trigger("Schedule: Daily lost-backlink scan", "schedule", {
        source: "seo_team",
        event: "backlink.lost_scan",
        schedule: "Every weekday at 7:00 AM",
        howItStarts:
          "Runs daily to detect recently lost backlinks using Ahrefs or similar API, categorize the loss type, determine the recovery strategy, and send personalized reclaim outreach to site owners",
        setup:
          "Set Ahrefs API key for backlink monitoring. Set OpenAI API key for reclaim email drafting. Configure contact discovery API (Hunter.io). Set email sending credentials. Set Google Sheets credentials for recovery tracking.",
        inputFields: [
          { name: "target_domain", type: "string", example: "company.com" },
          { name: "min_domain_rating", type: "string", example: "30" },
          { name: "lookback_days", type: "string", example: "7" },
        ],
      }),

      // 2. Lost-backlink detection API
      n("2", "hitApi", "Detect Lost Backlinks via Ahrefs", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.ahrefs.com/v3/site-explorer/backlinks?target={target_domain}&mode=subdomains&history=lost&limit=10&output=json",
          headers: [
            { key: "Authorization", value: "Bearer {ahrefs_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "backlinks[0].url_from",
              customName: "lost_link_source_url",
              type: "string",
            },
            {
              name: "backlinks[0].url_to",
              customName: "lost_link_target_url",
              type: "string",
            },
            {
              name: "backlinks[0].domain_rating_source",
              customName: "source_domain_rating",
              type: "number",
            },
            {
              name: "backlinks[0].anchor",
              customName: "lost_anchor_text",
              type: "string",
            },
            {
              name: "backlinks[0].first_seen",
              customName: "link_first_seen",
              type: "string",
            },
            {
              name: "backlinks[0].last_seen",
              customName: "link_last_seen",
              type: "string",
            },
            {
              name: "backlinks[0].http_code",
              customName: "source_http_code",
              type: "number",
            },
            {
              name: "backlinks.length",
              customName: "total_lost_links",
              type: "number",
            },
          ],
        },
      }),

      // 3. Link categorization runCode
      n("3", "runCode", "Categorize Link Loss", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Categorize the type of link loss and determine recovery approach
const sourceUrl = vars.lost_link_source_url || "";
const httpCode = parseInt(vars.source_http_code) || 200;
const dr = parseInt(vars.source_domain_rating) || 0;
const anchor = vars.lost_anchor_text || "";
const lastSeen = vars.link_last_seen || "";
let loss_type = "unknown";
let recovery_difficulty = "medium";
let is_worth_recovering = "false";
if (httpCode === 404) {
  loss_type = "page_removed";
  recovery_difficulty = "hard";
} else if (httpCode === 301 || httpCode === 302) {
  loss_type = "page_redirected";
  recovery_difficulty = "medium";
} else if (httpCode === 200) {
  loss_type = "link_removed_from_page";
  recovery_difficulty = "easy";
} else {
  loss_type = "site_issue";
  recovery_difficulty = "hard";
}
// Determine if worth recovering based on DR
if (dr >= parseInt(vars.min_domain_rating || "30")) {
  is_worth_recovering = "true";
}
const sourceDomain = sourceUrl.replace(/^https?:\\/\\//, "").split("/")[0].replace(/^www\\./, "");
const priority = dr >= 60 ? "high" : dr >= 40 ? "medium" : "low";
return { loss_type: loss_type, recovery_difficulty: recovery_difficulty, is_worth_recovering: is_worth_recovering, source_domain: sourceDomain, link_priority: priority };`,
          outputVariables: [
            { name: "loss_type", type: "string" },
            { name: "recovery_difficulty", type: "string" },
            { name: "is_worth_recovering", type: "string" },
            { name: "source_domain", type: "string" },
            { name: "link_priority", type: "string" },
          ],
        },
      }),

      // 4. Recovery-strategy selection condition
      n("4", "condition", "Is Link Worth Recovering?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_worth_recovering", operator: "eq", value: "true" },
          ],
        },
      }),

      // 5. Site-contact discovery API
      n("5", "hitApi", "Discover Site Contact via Hunter.io", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={source_domain}&api_key={hunter_api_key}&limit=3&department=marketing,executive",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "site_contact_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "site_contact_first",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "site_contact_last",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "site_contact_title",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 6. AI reclaim draft
      aiNode(
        "6",
        "AI Draft Reclaim Email",
        1240,
        "You are an SEO outreach specialist focused on backlink recovery. Write a polite, helpful reclaim email to a site owner about a lost link. Tailor your approach based on the loss type: if the page was removed, suggest the link still provides value to their readers; if redirected, offer updated URL; if the link was simply removed, ask politely and offer mutual value. Reference the specific page and anchor text. Keep under 150 words. Respond JSON: {reclaim_subject: string, reclaim_body: string}",
        "Lost link from: {lost_link_source_url} (DR: {source_domain_rating}). To: {lost_link_target_url}. Anchor: {lost_anchor_text}. Loss type: {loss_type}. HTTP code: {source_http_code}. First seen: {link_first_seen}. Last seen: {link_last_seen}. Contact: {site_contact_first}. Difficulty: {recovery_difficulty}. Write the reclaim email.",
        [
          { field: "reclaim_subject", as: "reclaim_subject", type: "string" },
          { field: "reclaim_body", as: "reclaim_body", type: "string" },
        ],
      ),

      // 7. Send + recovery tracker Sheets
      sheetsNode(
        "7",
        "Log Recovery Attempt to Tracker",
        1480,
        "BacklinkRecoveryTracker",
        '[["{source_domain}","{lost_link_source_url}","{lost_link_target_url}","{lost_anchor_text}","{source_domain_rating}","{loss_type}","{recovery_difficulty}","{site_contact_email}","{email_confidence}","sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1826. MEDDICC EXTRACTION FROM CALL (7 nodes)
  // ===================================================================

  "MEDDICC Extraction from Call": {
    summary:
      "Fires on webhook when a call transcript is ready, ingests the transcript via Gong API, extracts MEDDICC fields with AI, scores confidence with runCode, identifies gaps with runCode, updates the CRM MEDDICC fields in HubSpot, and briefs the rep on Slack.",
    nodes: [
      trigger("Webhook: Call transcript ready", "webhook_app", {
        source: "conversation_intelligence",
        event: "call.transcript_ready",
        howItStarts:
          "Fires when a call recording is transcribed (from Gong, Chorus, or similar) — automatically extracting MEDDICC qualification fields, scoring deal health, and updating the CRM with structured methodology data",
        setup:
          "Configure conversation intelligence webhook for transcript-ready events. Set Gong API credentials for transcript retrieval. Set OpenAI API key for MEDDICC extraction. Provide HubSpot token for CRM updates. Configure Slack webhook for rep briefings.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook
      aiNode(
        "2",
        "AI Extract Call Metadata",
        280,
        "You are a call data extraction specialist. Parse the raw webhook payload to find the call ID, deal ID, call participants, call date, call duration, and any summary or tags. Respond JSON: {call_id: string, deal_id: string, caller_name: string, prospect_name: string, prospect_company: string, call_date: string, call_duration_minutes: string, call_tags: string}",
        "Raw transcript-ready webhook payload: {webhook_payload}. Extract call metadata regardless of field naming conventions.",
        [
          { field: "call_id", as: "call_id", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "caller_name", as: "rep_name", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "call_date", as: "call_date", type: "string" },
          {
            field: "call_duration_minutes",
            as: "call_duration",
            type: "string",
          },
          { field: "call_tags", as: "call_tags", type: "string" },
        ],
      ),

      // 3. Transcript ingestion Gong API
      n("3", "hitApi", "Retrieve Full Transcript from Gong", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.gong.io/v2/calls/{call_id}/transcript",
          headers: [
            { key: "Authorization", value: "Bearer {gong_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "transcript.text",
              customName: "full_transcript",
              type: "string",
            },
            {
              name: "transcript.speakers",
              customName: "speakers_list",
              type: "string",
            },
            {
              name: "transcript.duration",
              customName: "transcript_duration",
              type: "string",
            },
            {
              name: "callMetadata.title",
              customName: "call_title",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI MEDDICC extraction
      aiNode(
        "4",
        "AI Extract MEDDICC Fields",
        760,
        "You are an elite sales methodology analyst specializing in MEDDICC. Analyze the call transcript and extract each MEDDICC component: Metrics (quantified business impact the prospect mentioned), Economic Buyer (who controls the budget), Decision Criteria (how they will evaluate solutions), Decision Process (steps to purchase), Identify Pain (explicit pain points mentioned), Champion (internal advocate signals), and Competition (competitors mentioned or implied). For each, provide the extracted evidence and a confidence level. Respond JSON: {metrics: string, metrics_confidence: number, economic_buyer: string, economic_buyer_confidence: number, decision_criteria: string, decision_criteria_confidence: number, decision_process: string, decision_process_confidence: number, identified_pain: string, pain_confidence: number, champion: string, champion_confidence: number, competition: string, competition_confidence: number}",
        "Transcript from call with {prospect_name} at {prospect_company}. Call title: {call_title}. Duration: {call_duration} min. Tags: {call_tags}. Full transcript: {full_transcript}. Extract all MEDDICC fields with evidence.",
        [
          { field: "metrics", as: "meddicc_metrics", type: "string" },
          {
            field: "metrics_confidence",
            as: "metrics_confidence",
            type: "number",
          },
          { field: "economic_buyer", as: "meddicc_econ_buyer", type: "string" },
          {
            field: "economic_buyer_confidence",
            as: "econ_buyer_confidence",
            type: "number",
          },
          {
            field: "decision_criteria",
            as: "meddicc_decision_criteria",
            type: "string",
          },
          {
            field: "decision_criteria_confidence",
            as: "criteria_confidence",
            type: "number",
          },
          {
            field: "decision_process",
            as: "meddicc_decision_process",
            type: "string",
          },
          {
            field: "decision_process_confidence",
            as: "process_confidence",
            type: "number",
          },
          { field: "identified_pain", as: "meddicc_pain", type: "string" },
          { field: "pain_confidence", as: "pain_confidence", type: "number" },
          { field: "champion", as: "meddicc_champion", type: "string" },
          {
            field: "champion_confidence",
            as: "champion_confidence",
            type: "number",
          },
          { field: "competition", as: "meddicc_competition", type: "string" },
          {
            field: "competition_confidence",
            as: "competition_confidence",
            type: "number",
          },
        ],
      ),

      // 5. Confidence scoring runCode
      n("5", "runCode", "Score Overall MEDDICC Confidence", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate overall MEDDICC confidence score
const scores = {
  M: parseInt(vars.metrics_confidence) || 0,
  E: parseInt(vars.econ_buyer_confidence) || 0,
  D1: parseInt(vars.criteria_confidence) || 0,
  D2: parseInt(vars.process_confidence) || 0,
  I: parseInt(vars.pain_confidence) || 0,
  C1: parseInt(vars.champion_confidence) || 0,
  C2: parseInt(vars.competition_confidence) || 0,
};
const weights = { M: 0.15, E: 0.2, D1: 0.15, D2: 0.1, I: 0.15, C1: 0.15, C2: 0.1 };
let weighted_score = 0;
let filled_count = 0;
for (const [key, score] of Object.entries(scores)) {
  weighted_score += score * weights[key];
  if (score >= 50) filled_count++;
}
const overall_score = Math.round(weighted_score);
const deal_health = overall_score >= 75 ? "strong" : overall_score >= 50 ? "developing" : overall_score >= 25 ? "early" : "at_risk";
const fields_filled = filled_count;
const completion_pct = Math.round((filled_count / 7) * 100);
return { meddicc_overall_score: overall_score, deal_health: deal_health, fields_filled: fields_filled, completion_pct: completion_pct, strongest_field: Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0], weakest_field: Object.entries(scores).sort((a, b) => a[1] - b[1])[0][0] };`,
          outputVariables: [
            { name: "meddicc_overall_score", type: "number" },
            { name: "deal_health", type: "string" },
            { name: "fields_filled", type: "number" },
            { name: "completion_pct", type: "number" },
            { name: "strongest_field", type: "string" },
            { name: "weakest_field", type: "string" },
          ],
        },
      }),

      // 6. Gap identification runCode
      n("6", "runCode", "Identify MEDDICC Gaps and Actions", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify gaps in MEDDICC and recommend next actions
const scores = {
  Metrics: parseInt(vars.metrics_confidence) || 0,
  Economic_Buyer: parseInt(vars.econ_buyer_confidence) || 0,
  Decision_Criteria: parseInt(vars.criteria_confidence) || 0,
  Decision_Process: parseInt(vars.process_confidence) || 0,
  Identified_Pain: parseInt(vars.pain_confidence) || 0,
  Champion: parseInt(vars.champion_confidence) || 0,
  Competition: parseInt(vars.competition_confidence) || 0,
};
const gaps = [];
const actions = [];
for (const [field, score] of Object.entries(scores)) {
  if (score < 50) {
    gaps.push(field.replace(/_/g, " "));
    if (field === "Metrics") actions.push("Ask prospect to quantify business impact in next call");
    else if (field === "Economic_Buyer") actions.push("Request introduction to budget holder");
    else if (field === "Decision_Criteria") actions.push("Send evaluation framework and ask for feedback");
    else if (field === "Decision_Process") actions.push("Map out procurement steps with champion");
    else if (field === "Identified_Pain") actions.push("Conduct deeper discovery on current challenges");
    else if (field === "Champion") actions.push("Identify and cultivate internal advocate");
    else if (field === "Competition") actions.push("Ask about alternatives being evaluated");
  }
}
const gap_summary = gaps.length > 0 ? gaps.join(", ") : "No significant gaps";
const action_plan = actions.length > 0 ? actions.join("; ") : "All fields adequately covered — focus on advancing deal stage";
return { gap_fields: gap_summary, gap_count: gaps.length, recommended_actions: action_plan, next_call_focus: gaps.length > 0 ? gaps[0] : "deal_advancement" };`,
          outputVariables: [
            { name: "gap_fields", type: "string" },
            { name: "gap_count", type: "number" },
            { name: "recommended_actions", type: "string" },
            { name: "next_call_focus", type: "string" },
          ],
        },
      }),

      // 7. Rep briefing Slack
      slackNode(
        "7",
        "Brief Rep on MEDDICC Results",
        1480,
        ":clipboard: *MEDDICC Extraction Complete*\n*Call:* {call_title} ({call_date})\n*Prospect:* {prospect_name} at {prospect_company}\n*Rep:* {rep_name} | *Duration:* {call_duration} min\n\n*Overall Score:* {meddicc_overall_score}/100 ({deal_health})\n*Completion:* {completion_pct}% ({fields_filled}/7 fields)\n*Strongest:* {strongest_field} | *Weakest:* {weakest_field}\n\n*M — Metrics:* {meddicc_metrics} (confidence: {metrics_confidence}%)\n*E — Economic Buyer:* {meddicc_econ_buyer} ({econ_buyer_confidence}%)\n*D — Decision Criteria:* {meddicc_decision_criteria} ({criteria_confidence}%)\n*D — Decision Process:* {meddicc_decision_process} ({process_confidence}%)\n*I — Identified Pain:* {meddicc_pain} ({pain_confidence}%)\n*C — Champion:* {meddicc_champion} ({champion_confidence}%)\n*C — Competition:* {meddicc_competition} ({competition_confidence}%)\n\n:warning: *Gaps:* {gap_fields}\n:dart: *Next Actions:* {recommended_actions}\n:point_right: *Next Call Focus:* {next_call_focus}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1827. MARKETO LEAD CREATE UPDATE (6 nodes)
  // ===================================================================

  "Marketo Lead Create Update": {
    summary:
      "Fires on webhook with lead data, checks for duplicates via Marketo API, normalizes data with runCode, decides create vs update via condition, executes the Marketo API call, and handles confirmation and errors with runCode.",
    nodes: [
      trigger("Webhook: Lead data received", "webhook_app", {
        source: "lead_ingestion",
        event: "lead.data_received",
        howItStarts:
          "Fires when new or updated lead data arrives from a form submission, integration, or data import — deduplicating against Marketo, normalizing fields, and creating or updating the lead record with proper error handling",
        setup:
          "Configure webhook to receive lead data from your ingestion pipeline. Set Marketo REST API credentials (client ID, client secret, Munchkin ID). Set OpenAI API key if needed for data parsing.",
        inputFields: [],
      }),

      // 2. AI extraction from webhook
      aiNode(
        "2",
        "AI Extract Lead Data",
        280,
        "You are a lead data extraction specialist. Parse the raw webhook payload to find lead information regardless of format. Extract email, first name, last name, company, phone, job title, lead source, and any custom fields present. Respond JSON: {lead_email: string, first_name: string, last_name: string, company: string, phone: string, job_title: string, lead_source: string, custom_fields: string}",
        "Raw lead data webhook payload: {webhook_payload}. Extract all lead fields regardless of naming conventions.",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company", as: "lead_company", type: "string" },
          { field: "phone", as: "lead_phone", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "custom_fields", as: "custom_fields", type: "string" },
        ],
      ),

      // 3. Marketo duplicate check API
      n("3", "hitApi", "Check for Duplicates in Marketo", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{marketo_munchkin_id}.mktorest.com/rest/v1/leads.json?filterType=email&filterValues={lead_email}&access_token={marketo_access_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "success", customName: "api_success", type: "string" },
            {
              name: "result[0].id",
              customName: "existing_lead_id",
              type: "string",
            },
            {
              name: "result[0].email",
              customName: "existing_email",
              type: "string",
            },
            {
              name: "result[0].firstName",
              customName: "existing_first_name",
              type: "string",
            },
            {
              name: "result[0].lastName",
              customName: "existing_last_name",
              type: "string",
            },
            {
              name: "result.length",
              customName: "match_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Data normalization runCode
      n("4", "runCode", "Normalize Lead Data", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize lead data fields for Marketo
const email = (vars.lead_email || "").toLowerCase().trim();
const firstName = (vars.lead_first_name || "").trim();
const lastName = (vars.lead_last_name || "").trim();
const company = (vars.lead_company || "").trim();
const phone = (vars.lead_phone || "").replace(/[^\\d+]/g, "");
const title = (vars.lead_title || "").trim();
const source = (vars.lead_source || "web").trim();
const matchCount = parseInt(vars.match_count) || 0;
const existingId = vars.existing_lead_id || "";
// Capitalize names properly
const normFirst = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
const normLast = lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase();
// Determine action
const action = matchCount > 0 ? "update" : "create";
const lookupId = matchCount > 0 ? existingId : "";
// Validate email format
const emailValid = email.includes("@") && email.includes(".") && email.length > 5;
// Build Marketo payload
const marketoPayload = JSON.stringify({
  action: action === "update" ? "updateOnly" : "createOnly",
  lookupField: "email",
  input: [{ email: email, firstName: normFirst, lastName: normLast, company: company, phone: phone, title: title, leadSource: source }],
});
return { normalized_email: email, normalized_first: normFirst, normalized_last: normLast, action_type: action, existing_id: lookupId, marketo_payload: marketoPayload, is_valid_email: emailValid ? "true" : "false", is_duplicate: matchCount > 0 ? "true" : "false" };`,
          outputVariables: [
            { name: "normalized_email", type: "string" },
            { name: "normalized_first", type: "string" },
            { name: "normalized_last", type: "string" },
            { name: "action_type", type: "string" },
            { name: "existing_id", type: "string" },
            { name: "marketo_payload", type: "string" },
            { name: "is_valid_email", type: "string" },
            { name: "is_duplicate", type: "string" },
          ],
        },
      }),

      // 5. Create update decision condition
      n("5", "condition", "Is This a Duplicate Lead?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_duplicate", operator: "eq", value: "true" },
          ],
        },
      }),

      // 6. Marketo API execute + confirmation/error handling runCode
      n("6", "hitApi", "Execute Marketo Create or Update", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_munchkin_id}.mktorest.com/rest/v1/leads.json?access_token={marketo_access_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "action", value: "{action_type}Only" },
            { key: "lookupField", value: "email" },
            {
              key: "input",
              value:
                '[{"email":"{normalized_email}","firstName":"{normalized_first}","lastName":"{normalized_last}","company":"{lead_company}","phone":"{lead_phone}","title":"{lead_title}","leadSource":"{lead_source}"}]',
            },
          ],
          variables: [
            { name: "success", customName: "marketo_success", type: "string" },
            {
              name: "result[0].id",
              customName: "marketo_lead_id",
              type: "string",
            },
            {
              name: "result[0].status",
              customName: "marketo_status",
              type: "string",
            },
            { name: "errors", customName: "marketo_errors", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
    ],
  },
};
